import type { Lang } from '../context/LanguageContext'

export interface Translations {
  nav: {
    home: string
    about: string
    services: string
    employers: string
    blog: string
    contacts: string
  }
  header: {
    cta: string
  }
  hero: {
    badge: string
    h1_1: string
    h1_accent: string
    h1_2: string
    subtitle: string
    cta_primary: string
    cta_secondary: string
    stat1_val: string
    stat1_label: string
    stat2_val: string
    stat2_label: string
    stat3_val: string
    stat3_label: string
    trust: string
  }
  hero_form: {
    title: string
    subtitle: string
    name_label: string
    name_placeholder: string
    phone_label: string
    position_label: string
    position_placeholder: string
    positions: string[]
    submit: string
    submitting: string
    success_title: string
    success_subtitle: string
    privacy: string
    errors: {
      name: string
      phone_required: string
      phone_invalid: string
      position: string
    }
  }
  achievements: {
    stats: Array<{ label: string; description: string; suffix: string }>
  }
  services: {
    eyebrow: string
    title: string
    subtitle: string
    badge: string
    card1_title: string
    card1_desc: string
    card1_stat1_val: string
    card1_stat1_label: string
    card1_stat2_val: string
    card1_stat2_label: string
    card2_title: string
    card2_desc: string
    card3_title: string
    card3_desc: string
    card3_case_val: string
    card3_case_label: string
    card4_title: string
    card4_desc: string
    card4_count: string
  }
  niches: {
    eyebrow: string
    title: string
    subtitle: string
    note: string
    positions_label: string
    other_positions: string
    categories: Array<{ title: string }>
  }
  process: {
    eyebrow: string
    title: string
    subtitle: string
    steps: Array<{ title: string; description: string }>
  }
  advantages: {
    eyebrow: string
    title: string
    subtitle: string
    stat1_label: string
    stat2_val: string
    stat2_label: string
    stat3_val: string
    stat3_label: string
    items: Array<{ title: string; description: string }>
  }
  ceo: {
    eyebrow: string
    role: string
    years_label: string
    quote: string
    points: Array<{ title: string; description: string }>
  }
  testimonials: {
    eyebrow: string
    title: string
    items: Array<{ quote: string; role: string }>
  }
  clients: {
    eyebrow: string
    title: string
  }
  faq: {
    eyebrow: string
    title: string
    subtitle: string
    items: Array<{ q: string; a: string }>
  }
  contact: {
    eyebrow: string
    title: string
    subtitle: string
    phone_label: string
    email_label: string
    messenger_label: string
    trust_badge: string
    form_title: string
    name_label: string
    name_placeholder: string
    company_label: string
    company_placeholder: string
    position_label: string
    position_placeholder: string
    phone_form_label: string
    phone_placeholder: string
    email_form_label: string
    email_placeholder: string
    submit: string
    submitting: string
    success_title: string
    success_subtitle: string
    send_another: string
    privacy: string
    errors: {
      name: string
      company: string
      position: string
      email_required: string
      email_invalid: string
      phone_required: string
      phone_invalid: string
      email_required: string
      email_invalid: string
    }
  }
  footer: {
    description: string
    address: string
    col1_title: string
    col1_links: string[]
    col2_title: string
    col2_links: string[]
    copyright: string
  }
  blog: {
    eyebrow: string
    title: string
    all_articles: string
    read_more: string
  }
  executiveSearch: {
    title: string
    badge: string
    h1_1: string
    h1_accent: string
    subtitle: string
    cta_primary: string
    cta_secondary: string
    stats: {
      days_val: string
      days_label: string
      guarantee_val: string
      guarantee_label: string
      closed_val: string
      closed_label: string
      success_val: string
      success_label: string
    }
    whatWeDo: {
      eyebrow: string
      title: string
      subtitle: string
      items: Array<{ title: string; desc: string; badge?: string }>
    }
    positions: {
      eyebrow: string
      title: string
      subtitle: string
      note: string
      items: string[]
      other: string
    }
    process: {
      eyebrow: string
      title: string
      steps: Array<{ title: string; desc: string }>
    }
    ctaBanner: {
      title: string
      subtitle: string
      button: string
    }
    formPositions: string[]
    faqItems: Array<{ q: string; a: string }>
  }
  itRecruitment: {
    title: string
    badge: string
    h1_1: string
    h1_accent: string
    subtitle: string
    cta_primary: string
    cta_secondary: string
    stats: {
      days_val: string
      days_label: string
      stacks_val: string
      stacks_label: string
      closed_val: string
      closed_label: string
      success_val: string
      success_label: string
    }
    stacks: {
      eyebrow: string
      title: string
      subtitle: string
    }
    whyUs: {
      eyebrow: string
      title: string
      subtitle: string
      points: Array<{ title: string; desc: string }>
      cards: Array<{ title: string; desc: string }>
    }
    ctaBanner: {
      title: string
      subtitle: string
      button: string
    }
    formPositions: string[]
    faqItems: Array<{ q: string; a: string }>
  }
  teamBuilding: {
    title: string
    badge: string
    h1_1: string
    h1_accent: string
    subtitle: string
    cta_primary: string
    cta_secondary: string
    stats: {
      time_val: string
      time_label: string
      teams_val: string
      teams_label: string
      guarantee_val: string
      guarantee_label: string
      size_val: string
      size_label: string
    }
    whatIsIncluded: {
      eyebrow: string
      title: string
      subtitle: string
      items: Array<{ title: string; desc: string; points: string[] }>
    }
    cases: {
      eyebrow: string
      title: string
      items: Array<{
        badge: string
        val: string
        val_label: string
        desc: string
        result: string
      }>
    }
    ctaBanner: {
      title: string
      subtitle: string
      button: string
    }
    formPositions: string[]
    faqItems: Array<{ q: string; a: string }>
  }
  careerConsulting: {
    title: string
    badge: string
    author: string
    h1: string
    h1_sub: string
    subtitle: string
    cta_primary: string
    cta_secondary: string
    stats: {
      lessons_val: string
      lessons_label: string
      time_val: string
      time_label: string
      exp_val: string
      exp_label: string
      price_val: string
      price_label: string
    }
    whatYouGet: {
      eyebrow: string
      title: string
      subtitle: string
      items: string[]
      free_lesson_title: string
      free_lesson_desc: string
    }
    modules: {
      eyebrow: string
      title: string
      subtitle: string
      items: Array<{
        num: string
        title: string
        topics: string[]
        extra?: string
      }>
    }
    pricing: {
      eyebrow: string
      title: string
      subtitle: string
      popular_badge: string
      plans: Array<{
        name: string
        price: string
        features: string[]
        cta: string
      }>
    }
    communities: {
      eyebrow: string
      title: string
      subtitle: string
      items: Array<{ name: string; desc: string }>
    }
    ctaBanner: {
      title: string
      subtitle: string
      button: string
    }
    faqItems: Array<{ q: string; a: string }>
  }
}

const UA: Translations = {
  nav: {
    home: 'Головна',
    about: 'Про компанію',
    services: 'Послуги',
    employers: 'Роботодавцям',
    blog: 'Блог',
    contacts: 'Контакти',
  },
  header: {
    cta: 'Залишити заявку',
  },
  hero: {
    badge: 'Понад 20 років на ринку рекрутингу',
    h1_1: 'Рекрутингова агенція з підбору персоналу',
    h1_accent: '',
    h1_2: '',
    subtitle: 'Пошук і підбір досвідчених спеціалістів для вашої компанії',
    cta_primary: 'Замовити підбір персоналу',
    cta_secondary: 'Наші галузі та спеціалізації',
    stat1_val: '3 500+',
    stat1_label: 'закритих вакансій',
    stat2_val: '98%',
    stat2_label: 'успішність підбору',
    stat3_val: '500+',
    stat3_label: 'клієнтів',
    trust: 'компаній довіряють нам',
  },
  achievements: {
    stats: [
      {
        label: 'Років досвіду',
        description:
          'Працюємо з 2013 року, накопичили експертизу у десятках галузей',
        suffix: '+',
      },
      {
        label: 'Закритих вакансій',
        description:
          'Від лінійних спеціалістів до C‑level позицій у різних індустріях',
        suffix: '+',
      },
      {
        label: 'Клієнтів',
        description:
          'Компанії від стартапів до міжнародних корпорацій довіряють нам підбір',
        suffix: '+',
      },
      {
        label: 'Успішність підбору',
        description:
          'Кандидати успішно залишаються в команді після випробувального терміну',
        suffix: '%',
      },
      {
        label: 'Середній термін закриття',
        description:
          'Швидкий підбір без втрати якості — економимо ваш час та ресурси',
        suffix: ' днів',
      },
      {
        label: 'Гарантія заміни',
        description:
          'Безкоштовна заміна кандидата, якщо він не підійшов протягом випробувального терміну',
        suffix: '%',
      },
    ],
  },
  services: {
    eyebrow: 'Що ми робимо',
    title: 'Наші послуги',
    subtitle:
      'Спеціалізуємося на пошуку ключових спеціалістів та побудові ефективних команд для вашого бізнесу',
    badge: 'Ключова спеціалізація',
    card1_title: 'Executive Search (C‑level)',
    card1_desc:
      'Пошук та підбір топ‑менеджерів: CEO, COO, CFO, CTO, CMO та інших керівників вищої ланки. Глибоке розуміння ринку та конфіденційний підхід до кожного проєкту.',
    card1_stat1_val: '14',
    card1_stat1_label: 'днів у середньому',
    card1_stat2_val: '100%',
    card1_stat2_label: 'гарантія заміни',
    card2_title: 'Підбір IT‑спеціалістів',
    card2_desc:
      'Розробники, DevOps, QA, Product Manager, дизайнери — знаємо специфіку IT‑ринку та говоримо однією мовою з кандидатами.',
    card3_title: 'Комплектація команди',
    card3_desc:
      'Формування цілих відділів та команд під ключ — від визначення структури до виходу останнього спеціаліста.',
    card3_case_val: '6 тижнів',
    card3_case_label: 'Зібрали IT‑відділ з 15 людей',
    card4_title: "Кар'єрні консультації",
    card4_desc:
      "Оцінка кар'єрних можливостей, підготовка до співбесід, стратегія розвитку та позиціонування на ринку.",
    card4_count: '200+ консультацій',
  },
  niches: {
    eyebrow: 'Спеціалізація',
    title: 'Ніші та напрямки',
    subtitle:
      'Підбираємо спеціалістів у ключових галузях — від лінійних позицій до топ‑менеджменту',
    note: 'Працюємо з усіма офісними та управлінськими позиціями. Не спеціалізуємося на робочих спеціальностях та масмаркеті.',
    positions_label: 'позицій',
    other_positions:
      'Та інші позиції у цьому напрямку — зверніться, і ми підберемо потрібного спеціаліста',
    categories: [
      { title: 'C‑level & Executive' },
      { title: 'IT & Digital' },
      { title: 'Фінанси та банкінг' },
      { title: 'Продажі та бізнес‑розвиток' },
      { title: 'Маркетинг та PR' },
      { title: 'HR & People' },
      { title: 'Логістика та операції' },
      { title: 'Юриспруденція' },
      { title: 'E‑commerce & Retail' },
      { title: 'Адміністрація та підтримка' },
    ],
  },
  process: {
    eyebrow: 'Як ми працюємо',
    title: 'Етапи підбору',
    subtitle:
      'Прозорий процес від першого дзвінка до виходу кандидата на роботу',
    steps: [
      {
        title: 'Бриф',
        description:
          'Вивчаємо потреби компанії, формуємо профіль ідеального кандидата',
      },
      {
        title: 'Вакансія',
        description: 'Складаємо привабливий опис та стратегію пошуку',
      },
      {
        title: 'Пошук',
        description:
          'Активний хантинг по базі 20 000+ кандидатів та відкритих джерелах',
      },
      {
        title: 'Відбір',
        description: 'Проводимо скринінг, оцінюємо компетенції та мотивацію',
      },
      {
        title: "Інтерв'ю",
        description:
          "Організовуємо зустрічі з фіналістами, збираємо зворотний зв'язок",
      },
      {
        title: 'Офер',
        description:
          'Допомагаємо узгодити умови та супроводжуємо вихід на роботу',
      },
    ],
  },
  advantages: {
    eyebrow: 'Чому ми',
    title: 'Наші переваги',
    subtitle:
      'Ми не просто закриваємо вакансії — ми будуємо команди, які рухають бізнес уперед',
    stat1_label:
      'кандидатів успішно залишаються в команді після випробувального терміну',
    stat2_val: '4.9',
    stat2_label: 'Рейтинг на Clutch',
    stat3_val: '14 днів',
    stat3_label: 'Середній time‑to‑hire',
    items: [
      {
        title: 'Широка база кандидатів',
        description:
          'Понад 20 000+ перевірених спеціалістів у нашій базі даних за всіма напрямками',
      },
      {
        title: 'Персональний підхід',
        description:
          'Персональний рекрутер для кожного проєкту, глибоке занурення у специфіку бізнесу',
      },
      {
        title: 'Економія часу та витрат',
        description:
          'Середній термін закриття — 14 днів. Ви економите ресурси на пошук та відбір',
      },
      {
        title: 'Прозорі ціни',
        description:
          'Фіксована вартість без прихованих платежів. Оплата за фактом виходу кандидата',
      },
      {
        title: 'Гарантія заміни',
        description:
          'Якщо кандидат не підійшов протягом випробувального терміну — підберемо заміну безкоштовно',
      },
      {
        title: 'Аналітика та звітність',
        description:
          'Щотижневі звіти про хід підбору, аналітика ринку та рекомендації щодо умов',
      },
    ],
  },
  ceo: {
    eyebrow: 'Засновник агентства',
    role: 'CEO & Founder ESCA',
    years_label: 'років на ринку',
    quote:
      '«Ми знаємо все про болі компаній по формуванню кадрового складу будучи безпосередньо у реальному операційному бізнесі. Наше завдання — швидко та якісно вирішити ваше питання та знайти спеціаліста максимально відповідного для вашої вакансії!»',
    points: [
      {
        title: 'Реальний бізнес‑досвід.',
        description:
          'Розуміння потреб компаній зсередини — не теоретично, а з практики управління операційним бізнесом.',
      },
      {
        title: 'Executive Search експертиза.',
        description:
          'Спеціалізація на пошуку C‑level та ключових спеціалістів для бізнесу різних масштабів.',
      },
      {
        title: 'Персональна залученість.',
        description:
          'Особисто контролює ключові проєкти та гарантує якість підбору на кожному етапі.',
      },
    ],
  },
  testimonials: {
    eyebrow: 'Відгуки клієнтів',
    title: 'Нам довіряють',
    items: [
      {
        quote:
          'Найбільш вражаюче та унікальне в цій компанії — чудові комунікативні навички, їхнє розуміння вакансій та підхід до пошуку кандидатів. Працювати з ESCA було справжнім задоволенням, і ми з нетерпінням чекаємо на подальшу співпрацю. Ніколи не зупиняйтесь і продовжуйте зростати!',
        role: 'Founder, Code-Smith',
      },
      {
        quote:
          "Ми обрали ESCA серед кількох рекрутингових компаній, які знайшли в інтернеті. Вирішальними факторами для нас стали готовність закрити всі наші відкриті позиції та найбільш підходяща цінова модель. Підрядники, яких ESCA допомогла нам знайти, були з різних сфер, але переважно з IT/розробки. Переважна більшість людей, яких ми знайшли через ESCA, успішно продовжують кар'єру в нашій компанії. Серед них — Support Agents, DevOps-інженери (k8s та GCP), Python-розробники, PHP-розробники, Front-end розробники (React).",
        role: 'CTO Representative, SaaS Company',
      },
      {
        quote:
          'Сергій та його команда - компанія ESCA підібрали розробників для нашої компанії. Проект був завершений вчасно та в рамках узгодженого обсягу. Робота була виконана якісно. Ми залишилися дуже задоволені результатом. Рекомендуємо як відповідальне рекрутингове агентство.',
        role: 'CEO, Dizair',
      },
      {
        quote:
          'ESCA — це дуже відповідальні партнери, які мають високий рівень виконання задач. Ми постійно розвиваємося та наймаємо нових розробників. ESCA допомагає нам вже 1,5 роки.',
        role: 'PM, Andromo LTD',
      },
      {
        quote:
          'Ми скористалися послугами ESCA для найму HRD та CFO. Ми задоволені всім процесом співпраці.',
        role: 'Managing Partner, SBSB Fintech Lawyers',
      },
      {
        quote:
          "У процесі роботи та спілкування були враховані всі наші вимоги до кандидатів. Менеджер завжди відповідав на наші повідомлення протягом 5-10 хвилин, і завжди був на зв'язку по телефону. Я повністю задоволений результатами роботи! Бажаю ESCA подальшого зростання та розвитку!",
        role: 'Co-Founder, para.agency',
      },
    ],
  },
  clients: {
    eyebrow: 'Нам довіряють',
    title: 'Компанії, для яких ми закривали позиції',
  },
  faq: {
    eyebrow: 'Питання та відповіді',
    title: 'Часті питання',
    subtitle:
      'Відповідаємо на те, що клієнти запитують найчастіше перед стартом співпраці',
    items: [
      {
        q: 'Скільки коштують ваші послуги?',
        a: 'Вартість залежить від рівня позиції та складності пошуку. Для лінійних та IT-спеціалістів ми працюємо за фіксованою ставкою, для C-level — за відсотком від річного доходу кандидата. Точну вартість називаємо після брифу: він безкоштовний і ні до чого не зобовʼязує.',
      },
      {
        q: 'Що буде, якщо кандидат не пройде випробувальний термін?',
        a: 'Ми надаємо гарантію заміни. Якщо кандидат звільняється або не проходить випробувальний термін, ми безкоштовно шукаємо заміну на цю ж позицію. Строк гарантії фіксується в договорі до старту роботи.',
      },
      {
        q: 'Скільки часу займає закриття вакансії?',
        a: 'У середньому 14 днів. Перших релевантних кандидатів ви отримуєте вже через 3–5 робочих днів після затвердження профілю. Складні C-level та рідкісні технічні позиції можуть займати до 4–6 тижнів.',
      },
      {
        q: 'Ви працюєте з моєю галуззю?',
        a: 'Ми закриваємо позиції в 10 напрямках: C-level, IT та Digital, фінанси, продажі, маркетинг, HR, логістика, юриспруденція, e-commerce та адміністрація. За 20 років ми працювали з понад 500 компаніями — від стартапів до міжнародних груп.',
      },
      {
        q: 'Що якщо ви не знайдете кандидата?',
        a: 'Ми працюємо за передоплатною або поетапною моделлю залежно від позиції. Якщо пошук не дав результату, ви не платите фінальну частину винагороди. Умови завжди прописані в договорі до старту.',
      },
      {
        q: 'Як ви шукаєте кандидатів?',
        a: 'Використовуємо власну базу з 20 000+ контактів, прямий хедхантинг, професійні спільноти та рекомендації. Ми не обмежуємось відгуками з job-бордів — більшість сильних кандидатів не шукає роботу активно.',
      },
      {
        q: 'Чи потрібно підписувати договір?',
        a: 'Так. Ми працюємо офіційно за договором, у якому фіксуються вартість, строки, гарантія заміни та умови конфіденційності. Для чутливих пошуків підписуємо NDA.',
      },
      {
        q: 'З чого почати співпрацю?',
        a: 'Залиште заявку — ми звʼяжемось протягом 15 хвилин, проведемо бриф на 30 хвилин і надішлемо комерційну пропозицію з термінами та вартістю. Бриф безкоштовний.',
      },
    ],
  },
  contact: {
    eyebrow: "Зв'яжіться з нами",
    title: 'Залишити заявку',
    subtitle:
      "Розкажіть про вашу задачу — ми зв'яжемося протягом 15 хвилин та запропонуємо рішення",
    phone_label: 'Телефон',
    email_label: 'E‑mail',
    messenger_label: 'Або напишіть нам у месенджер:',
    trust_badge: 'Відповідаємо протягом 15 хвилин у робочий час',
    form_title: 'Заповніть форму',
    name_label: "Ім'я *",
    name_placeholder: "Ваше ім'я",
    company_label: 'Компанія *',
    company_placeholder: 'Назва компанії',
    position_label: 'Кого ви шукаєте? *',
    position_placeholder:
      'Наприклад: CTO, керівник відділу продажів, Senior Java Developer',
    phone_form_label: 'Телефон *',
    phone_placeholder: '+38 (0XX) XXX‑XX‑XX',
    email_form_label: 'E‑mail (необовʼязково)',
    email_placeholder: 'your@email.com',
    submit: 'Надіслати заявку',
    submitting: 'Надсилання...',
    success_title: 'Заявку надіслано!',
    success_subtitle: "Ми зв'яжемося з вами протягом 15 хвилин у робочий час",
    send_another: 'Надіслати ще одну заявку',
    privacy:
      'Натискаючи «Надіслати», ви погоджуєтесь з політикою конфіденційності',
    errors: {
      name: "Введіть ім'я",
      company: 'Введіть назву компанії',
      position: 'Вкажіть, кого ви шукаєте',
      phone_required: 'Введіть телефон',
      phone_invalid: 'Некоректний номер',
      email_required: 'Введіть e‑mail',
      email_invalid: 'Некоректний e‑mail',
    },
  },
  footer: {
    description:
      'Executive Search Consulting Agency. Допомагаємо бізнесу знаходити найкращих спеціалістів, а професіоналам — роботу мрії.',
    address: '04053, м. Київ, вул. Кудрявська 3/5',
    col1_title: 'Компанія',
    col1_links: ['Про нас', 'Послуги', 'Контакти'],
    col2_title: 'Послуги',
    col2_links: [
      'Executive Search (C‑level)',
      'Підбір IT‑спеціалістів',
      'Комплектація команди',
      "Курс «Кар'єра»",
    ],
    copyright: '© 2026 ESCA. Усі права захищені.',
  },
  blog: {
    eyebrow: 'Блог',
    title: 'Статті та кейси',
    all_articles: 'Усі статті',
    read_more: 'Читати далі',
  },
  executiveSearch: {
    title: 'Executive Search',
    badge: 'Ключова спеціалізація',
    h1_1: 'Executive Search',
    h1_accent: '(C‑level)',
    subtitle:
      'Пошук та підбір топ‑менеджерів: CEO, COO, CFO, CTO, CMO та інших керівників вищої ланки. Глибоке розуміння ринку та конфіденційний підхід до кожного проєкту.',
    cta_primary: 'Залишити заявку',
    cta_secondary: 'Як ми працюємо',
    stats: {
      days_val: '14',
      days_label: 'днів у середньому',
      guarantee_val: '100%',
      guarantee_label: 'гарантія заміни',
      closed_val: '500+',
      closed_label: 'C‑level закрито',
      success_val: '98%',
      success_label: 'успішність підбору',
    },
    whatWeDo: {
      eyebrow: 'Що входить у послугу',
      title: 'Повний цикл пошуку топ‑менеджерів',
      subtitle:
        'Від формування профілю до виходу кандидата — ми супроводжуємо весь процес',
      items: [
        {
          title: 'Широкий пошук кандидатів',
          desc: 'Використовуємо всі доступні джерела: власні 5 спільнот та 4 групи топ-менеджерів, відкриті майданчики, LinkedIn та прямий хантинг (за домовленістю).',
        },
        {
          title: 'Глибоке розуміння ринку',
          desc: 'Знаємо ринок топ-менеджерів у ключових галузях — хто є, де працює, яка репутація. Це дозволяє швидко виходити на потрібних кандидатів без зайвих витрат часу.',
        },
        {
          title: 'Перевірка референсів',
          desc: 'Збір відгуків від колишніх роботодавців та колег — доступно як додаткова опція за окремою домовленістю.',
          badge: 'Опційно',
        },
        {
          title: 'Оцінка відповідності',
          desc: "Проводимо інтерв'ю з кандидатами, оцінюємо відповідність вимогам позиції, досвід та мотивацію. Передаємо клієнту тих, хто реально підходить.",
        },
        {
          title: 'Переговори та офер',
          desc: 'Допомагаємо структурувати компенсаційний пакет, ведемо переговори та супроводжуємо підписання контракту.',
        },
        {
          title: 'Гарантія заміни',
          desc: 'Якщо кандидат не підійшов протягом випробувального терміну — підберемо заміну безкоштовно. Без зайвих питань.',
        },
      ],
    },
    positions: {
      eyebrow: 'Позиції',
      title: 'Яких керівників ми підбираємо',
      subtitle:
        'Спеціалізуємося на пошуку топ‑менеджерів для компаній будь‑якого масштабу — від стартапів до міжнародних корпорацій.',
      note: 'Середній досвід наших кандидатів — 15+ років у галузі. Ми не підбираємо «перспективних» — ми знаходимо тих, хто вже довів свою ефективність.',
      items: [
        'CEO / Генеральний директор',
        'COO / Операційний директор',
        'CFO / Фінансовий директор',
        'CTO / Технічний директор',
        'CMO / Директор з маркетингу',
        'CHRO / HR директор',
        'CSO / Директор з продажів',
        'CDO / Директор з цифрової трансформації',
        'Board Member / Член ради директорів',
        'Country Manager',
        'VP of Engineering',
        'VP of Product',
      ],
      other:
        'Та інші позиції у цьому напрямку — зверніться, і ми підберемо потрібного спеціаліста',
    },
    process: {
      eyebrow: 'Процес',
      title: 'Як ми знаходимо топ‑менеджерів',
      steps: [
        {
          title: 'Стратегічний бриф',
          desc: 'Глибоке занурення у бізнес клієнта. Вивчаємо стратегію, культуру, команду та очікування від нового керівника.',
        },
        {
          title: 'Формування лонг‑ліста',
          desc: 'Визначаємо пріоритетні джерела пошуку, формуємо список потенційних кандидатів з обґрунтуванням кожного.',
        },
        {
          title: 'Пошук та хантинг',
          desc: 'Активний пошук через власні спільноти, відкриті джерела та прямий вихід на цільових кандидатів.',
        },
        {
          title: "Інтерв'ю та відбір",
          desc: "Проводимо інтерв'ю з кандидатами, оцінюємо відповідність позиції та мотивацію. Відбираємо найкращих.",
        },
        {
          title: 'Презентація кандидатів',
          desc: "Представляємо фіналістів клієнту з детальними профілями. Організовуємо зустрічі та збираємо зворотний зв'язок.",
        },
      ],
    },
    ctaBanner: {
      title: 'Потрібен C‑level керівник?',
      subtitle:
        "Розкажіть про вашу задачу — ми зв'яжемося протягом 15 хвилин та запропонуємо план дій.",
      button: 'Залишити заявку',
    },
    formPositions: [
      'CEO / Генеральний директор',
      'COO / Операційний директор',
      'CFO / Фінансовий директор',
      'CTO / Технічний директор',
      'CMO / Директор з маркетингу',
      'CHRO / HR директор',
      'CSO / Директор з продажів',
      'CDO / Директор з цифрової трансформації',
      'VP of Engineering',
      'VP of Product',
      'Country Manager',
      'Board Member / Радник',
      'Інша C‑level позиція',
    ],
    faqItems: [
      {
        q: 'Чим Executive Search відрізняється від звичайного рекрутингу?',
        a: 'Ми не публікуємо вакансію і не чекаємо відгуків. Сильні керівники не шукають роботу активно — ми складаємо карту ринку, виходимо на них напряму та ведемо переговори конфіденційно. Це прямий цільовий пошук, а не робота з вхідним потоком.',
      },
      {
        q: 'Скільки коштує підбір керівника?',
        a: 'Для C-level ми працюємо за відсотком від річного доходу кандидата. Точна ставка залежить від рівня позиції, галузі та географії пошуку. Вартість фіксуємо в договорі після брифу — він безкоштовний.',
      },
      {
        q: 'Скільки часу займає пошук топ-менеджера?',
        a: 'Від 3 до 6 тижнів залежно від рівня позиції. Перший лонгліст із карти ринку ви отримуєте протягом 7–10 днів, шортліст із 3–5 фіналістів — до кінця третього тижня.',
      },
      {
        q: 'Чи гарантуєте ви результат?',
        a: 'Так. Ми надаємо гарантію заміни: якщо керівник залишає компанію протягом гарантійного періоду, ми проводимо повторний пошук безкоштовно. Строк гарантії для C-level фіксується в договорі.',
      },
      {
        q: 'Чи можна зберегти пошук у таємниці від ринку?',
        a: 'Так, це стандартна практика для Executive Search. Ми підписуємо NDA і ведемо пошук без розкриття назви компанії до етапу фінальних переговорів. Це критично, якщо позиція ще зайнята.',
      },
      {
        q: 'Як ви оцінюєте кандидатів рівня C-level?',
        a: 'Крім глибинного інтервʼю, ми перевіряємо реальні бізнес-результати кандидата, збираємо рекомендації від колишніх керівників і партнерів, оцінюємо відповідність корпоративній культурі. Ви отримуєте розгорнутий звіт по кожному фіналісту.',
      },
    ],
  },
  itRecruitment: {
    title: 'Підбір IT‑спеціалістів',
    badge: 'IT Recruitment',
    h1_1: 'Підбір',
    h1_accent: 'IT‑спеціалістів',
    subtitle:
      'Розробники, DevOps, QA, Product Manager, дизайнери — знаємо специфіку IT‑ринку та говоримо однією мовою з кандидатами.',
    cta_primary: 'Залишити заявку',
    cta_secondary: 'Наші стеки',
    stats: {
      days_val: '14',
      days_label: 'днів у середньому',
      stacks_val: '50+',
      stacks_label: 'технологічних стеків',
      closed_val: '1500+',
      closed_label: 'IT‑вакансій закрито',
      success_val: '98%',
      success_label: 'успішність підбору',
    },
    stacks: {
      eyebrow: 'Технології',
      title: 'Стеки та спеціалізації',
      subtitle:
        'Підбираємо спеціалістів будь‑якого рівня — від junior до tech lead',
    },
    whyUs: {
      eyebrow: 'Чому ми',
      title: 'Ми розуміємо IT зсередини',
      subtitle:
        'Наші рекрутери мають технічний бекграунд та пройшли спеціалізоване навчання. Ми не просто читаємо резюме — ми розуміємо, що стоїть за кожним рядком.',
      points: [
        {
          title: 'Технічний скринінг',
          desc: 'Перевіряємо реальні навички, а не лише список технологій у резюме',
        },
        {
          title: 'Активний хантинг',
          desc: 'Знаходимо навіть пасивних кандидатів, які не шукають роботу активно',
        },
        {
          title: 'Ринкова аналітика',
          desc: 'Надаємо актуальні дані по зарплатах та ринку IT‑спеціалістів',
        },
        {
          title: 'Швидкість',
          desc: 'Перші кандидати — вже на 3–5 день після старту пошуку',
        },
      ],
      cards: [
        {
          title: 'Швидкий старт',
          desc: 'Запускаємо пошук протягом 24 годин після підписання договору',
        },
        {
          title: 'База 20 000+ кандидатів',
          desc: 'Власна база перевірених IT‑спеціалістів, яка поповнюється щодня',
        },
        {
          title: 'Прозора комунікація',
          desc: 'Щотижневі звіти, доступ до воронки кандидатів у реальному часі',
        },
      ],
    },
    ctaBanner: {
      title: 'Потрібен IT‑спеціаліст?',
      subtitle:
        "Опишіть вашу вакансію — ми зв'яжемося протягом 15 хвилин та запропонуємо план пошуку.",
      button: 'Залишити заявку',
    },
    formPositions: [
      'JavaScript / TypeScript Developer',
      'React Developer',
      'Vue / Angular Developer',
      'Node.js Developer',
      'Python Developer',
      'Java Developer',
      'C# / .NET Developer',
      'PHP Developer',
      'Go Developer',
      'Ruby Developer',
      'C / C++ Developer',
      'Rust Developer',
      'Fullstack Developer',
      'iOS Developer (Swift)',
      'Android Developer (Kotlin / Java)',
      'React Native / Flutter Developer',
      'DevOps / SRE / Cloud Engineer',
      'QA Engineer (Manual / Automation)',
      'Data Engineer',
      'Data Scientist / ML Engineer',
      'AI / LLM Engineer',
      'Solution / Cloud Architect',
      'Cybersecurity Engineer',
      'DBA / Database Engineer',
      'System / Network адміністратор',
      'Embedded / IoT Developer',
      'Game Developer',
      'Blockchain / Web3 Developer',
      'UX/UI / Product Designer',
      'Product Manager',
      'Project / Delivery Manager',
      'Business Analyst',
      'Scrum Master / Agile Coach',
      'Tech Lead / Team Lead',
      'CTO / Head of Engineering / VP Engineering',
      'Інша IT‑позиція',
    ],
    faqItems: [
      {
        q: 'Чи розбираються ваші рекрутери в технологіях?',
        a: 'Так. Наші IT-рекрутери розуміють різницю між стеками, читають технічні вимоги і не надсилають Java-розробника на вакансію JavaScript. За потреби ми залучаємо технічного експерта для попереднього скринінгу.',
      },
      {
        q: 'Скільки часу займає закриття IT-вакансії?',
        a: 'У середньому 14 днів. Перші релевантні кандидати — через 3–5 робочих днів. Рідкісні стеки (Rust, Scala, ML-інженери сеньйорного рівня) можуть займати 3–5 тижнів.',
      },
      {
        q: 'Ви шукаєте тільки в Україні?',
        a: 'Ні. Ми працюємо з українським ринком, польським, країнами Балтії, а також із кандидатами з релокацією та в форматі remote. Географія залежить від вашої моделі найму.',
      },
      {
        q: 'Що якщо розробник піде через два місяці?',
        a: 'Діє гарантія заміни. Якщо спеціаліст звільняється в межах гарантійного періоду, ми знаходимо заміну безкоштовно. Строк гарантії прописаний у договорі.',
      },
      {
        q: 'Скільки коштує підбір IT-спеціаліста?',
        a: 'Для IT-позицій ми пропонуємо фіксовану ставку або відсоток від річного доходу — залежно від обсягу. При наймі команди від 3 осіб діють кращі умови. Вартість озвучуємо після брифу.',
      },
      {
        q: 'Чи можете ви взяти на себе технічне інтервʼю?',
        a: 'Ми проводимо повний скринінг: релевантність досвіду, стек, англійська, мотивація та очікування по компенсації. Фінальне технічне інтервʼю зазвичай проводить ваша команда — але ми можемо організувати зовнішнього технічного інтервʼюера.',
      },
    ],
  },
  teamBuilding: {
    title: 'Комплектація команди',
    badge: 'Команди під ключ',
    h1_1: 'Комплектація',
    h1_accent: 'команди',
    subtitle:
      'Формування цілих відділів та команд під ключ — від визначення структури до виходу останнього спеціаліста на роботу.',
    cta_primary: 'Залишити заявку',
    cta_secondary: 'Наші кейси',
    stats: {
      time_val: '6',
      time_label: 'тижнів — команда з 15 людей',
      teams_val: '27+',
      teams_label: 'команд сформовано',
      guarantee_val: '100%',
      guarantee_label: 'гарантія заміни',
      size_val: '3–50',
      size_label: 'людей у команді',
    },
    whatIsIncluded: {
      eyebrow: 'Що входить',
      title: 'Повний цикл формування команди',
      subtitle:
        'Від аналізу потреб до онбордингу останнього спеціаліста — ми беремо на себе весь процес',
      items: [
        {
          title: 'Організаційне проєктування',
          desc: 'Допомагаємо визначити оптимальну структуру команди, ролі та зону відповідальності ще до початку пошуку.',
          points: [
            'Аналіз бізнес‑потреб та цілей',
            'Розробка організаційної структури',
            'Визначення пріоритетності найму',
          ],
        },
        {
          title: 'Паралельний пошук',
          desc: 'Одночасно ведемо пошук за всіма позиціями — це скорочує загальний термін формування команди на 40%.',
          points: [
            'Виділена команда рекрутерів',
            'Спеціалізація за напрямками',
            'Єдиний дедлайн для всіх позицій',
          ],
        },
        {
          title: 'Координація процесу',
          desc: 'Один менеджер проєкту координує весь процес. Ви отримуєте єдину точку контакту та повну прозорість.',
          points: [
            'Щотижневі статус‑зустрічі',
            'Дашборд прогресу в реальному часі',
            'Проактивна комунікація',
          ],
        },
        {
          title: 'Гарантія результату',
          desc: 'Фіксуємо дедлайни у договорі. Гарантія безкоштовної заміни для кожного члена команди.',
          points: [
            'Фіксований дедлайн у договорі',
            '100% гарантія заміни',
            'Підтримка після виходу команди',
          ],
        },
      ],
    },
    cases: {
      eyebrow: 'Кейси',
      title: 'Команди, які ми зібрали',
      items: [
        {
          badge: 'Фінтех',
          val: '15',
          val_label: 'спеціалістів за 6 тижнів',
          desc: 'IT‑відділ для фінтех‑стартапу після раунду Series A. Frontend, Backend, DevOps, QA та Team Lead.',
          result: '✓ 100% пройшли випробувальний термін',
        },
        {
          badge: 'Рітейл',
          val: '8',
          val_label: 'менеджерів за 4 тижні',
          desc: 'Відділ продажів для e‑commerce компанії. Регіональні менеджери та керівник відділу.',
          result: '✓ +35% до виручки за квартал',
        },
        {
          badge: 'Логістика',
          val: '12',
          val_label: 'операційних менеджерів за 5 тижнів',
          desc: 'Операційний відділ для логістичної компанії при масштабуванні на нові регіони.',
          result: '✓ Вихід на 3 нові ринки',
        },
      ],
    },
    ctaBanner: {
      title: 'Потрібна команда під ключ?',
      subtitle:
        'Розкажіть про вашу задачу — ми розробимо план формування команди та запропонуємо терміни.',
      button: 'Залишити заявку',
    },
    formPositions: [
      'IT‑команда (Frontend + Backend + QA + DevOps)',
      'Команда продажів',
      'Команда маркетингу',
      'Команда продукту (PM, Designer, BA)',
      'Фінансова команда',
      'HR‑команда',
      'Операційна команда / Бек‑офіс',
      'Customer Support команда',
      'Стартап з нуля (всі ключові ролі)',
      'Інший склад команди',
    ],
    faqItems: [
      {
        q: 'Скільки часу займає збір команди під ключ?',
        a: 'Команду з 5–8 спеціалістів ми формуємо за 4–8 тижнів залежно від складності ролей. Ми не шукаємо всіх послідовно — паралельно ведемо кілька напрямків, тому команда виходить на роботу майже одночасно.',
      },
      {
        q: 'Чим це вигідніше, ніж наймати кожного окремо?',
        a: 'Ви економите на вартості за позицію, отримуєте узгоджену за рівнем і культурою команду, а не набір випадкових людей, і не витрачаєте місяці менеджерського часу на десятки інтервʼю. При обсязі від 3 позицій діють спеціальні умови.',
      },
      {
        q: 'Ви допомагаєте побудувати структуру команди?',
        a: 'Так. Якщо у вас ще немає чіткого розуміння складу, ми на брифі допоможемо визначити необхідні ролі, рівні (junior/middle/senior) та порядок найму — виходячи з ваших задач і бюджету.',
      },
      {
        q: 'Що якщо частина команди не приживеться?',
        a: 'Гарантія заміни діє на кожну позицію окремо. Якщо хтось із команди звільняється в гарантійний період, ми знаходимо заміну безкоштовно, не зачіпаючи умови по інших ролях.',
      },
      {
        q: 'Чи можна почати з частини команди?',
        a: 'Так. Багато клієнтів починають із ключових ролей (тимлід, senior-спеціалісти), а потім добирають решту. Ми складаємо поетапний план найму під ваш темп зростання та бюджет.',
      },
      {
        q: 'Скільки це коштує?',
        a: 'Вартість розраховується під конкретний склад команди. Чим більше позицій — тим нижча ставка за кожну. Комерційну пропозицію з фіксованою сумою і строками надсилаємо після безкоштовного брифу.',
      },
    ],
  },
  careerConsulting: {
    title: "Курс «Кар'єра»",
    badge: 'Авторський онлайн-курс',
    author: 'Авторський онлайн-курс Сергія Денисюка',
    h1: "КАР'ЄРА",
    h1_sub: 'Як досягти бажаної посади найефективнішим шляхом',
    subtitle:
      'CEO і власника міжнародної агенції з підбором керівників ESCA. Жодної «води» — тільки суть. Повне перепакування за два тижні.',
    cta_primary: 'Отримати вступний урок безкоштовно',
    cta_secondary: 'Тарифи',
    stats: {
      lessons_val: '5',
      lessons_label: 'відео-уроків',
      time_val: '2',
      time_label: 'тижні до результату',
      exp_val: '20+',
      exp_label: 'років досвіду автора',
      price_val: 'від $300',
      price_label: 'вартість курсу',
    },
    whatYouGet: {
      eyebrow: 'Що входить до курсу',
      title: 'Завдання — повністю перепакувати вас за два тижні',
      subtitle:
        "Жодної «води». Тільки суть. За результатами проходження у вас буде резюме, що продає, стратегія досягнення потрібної позиції та розуміння всіх нюансів побудови кар'єри.",
      items: [
        '5 відео-уроків з можливістю перегляду у зручний час',
        'Домашні завдання з перевіркою (крім тарифу «Стандартний»)',
        'Розбір реальних кейсів із практики',
        'Список сайтів та чатів вакансій топ-менеджерів, сайтів з рівнями доходів та відгуками про роботодавців',
        'Список рекомендованої літератури',
        'Відео-зустріч з автором курсу (крім тарифу «Стандартний»). У тарифі «Преміум» — 5 фідбек-сесій',
        'Резюме учасників курсу потрапляють до бази ESCA — ми працюємо з ними при надходженні релевантних запитів',
      ],
      free_lesson_title: 'Безкоштовний вступний урок',
      free_lesson_desc:
        "Залиште заявку у формі нижче — і ми надішлемо вступний урок до курсу «Кар'єра»",
    },
    modules: {
      eyebrow: 'Програма',
      title: 'Структура курсу',
      subtitle:
        '5 відео-уроків + відео-зустріч з автором. Два відео-уроки на тиждень — старт протягом доби після оплати.',
      items: [
        {
          num: '01',
          title:
            "Кар'єрна стратегія. Побудова власного бренду. Тайм-менеджмент",
          topics: [
            'Визначення точки старту та мети',
            "Типи кар'єри та стратегія досягнення мети",
            "Особиста кар'єрна перевага",
            'Побудова особистого бренду',
            'Мистецтво тайм-менеджменту',
          ],
        },
        {
          num: '02',
          title: 'Підготовка резюме. Створення резюме, що продає',
          topics: [
            'Розмір резюме та його формат',
            'Яка інформація має бути у резюме',
            'Як відбувається відбір резюме',
            'Секрети та лайфхаки резюме, що продає',
            'Приклади типових помилок у реальних резюме',
          ],
        },
        {
          num: '03',
          title: 'Пошук найкращих вакансій. Зміна сфери',
          topics: [
            'Шляхи пошуку роботи',
            'Використання LinkedIn та Facebook',
            'Нетворкінг для пошуку роботи',
            'Рекомендації щодо зміни сфери',
            'Типові помилки під час пошуку',
          ],
          extra: 'Додатково: сайти та канали вакансій',
        },
        {
          num: '04',
          title: "Підготовка до інтерв'ю. Переговори щодо доходу",
          topics: [
            'Як отримати повну інформацію про компанію',
            'Підготовка самопрезентації та до можливих питань',
            "Відмінності в інтерв'ю з агентствами, HR та керівництвом",
            "Нюанси онлайн-інтерв'ю",
            'Оцінка вартості на ринку та обговорення зарплати',
          ],
          extra:
            'Додатково: сайти за рівнем доходів та відгуками про роботодавців',
        },
        {
          num: '05',
          title: "Випробувальний термін. Кар'єрне зростання. Найм vs бізнес",
          topics: [
            'Успішний старт та план на випробувальний термін',
            'Що робити, якщо робота не відповідає очікуванням',
            'Оцінка перспектив зростання в компанії',
            'Робота за наймом чи власний бізнес — що зважити',
          ],
        },
        {
          num: '06',
          title: 'Відео-зустріч з автором курсу',
          topics: [
            'Індивідуальна сесія з Сергієм Денисюком',
            'Відповіді на особисті запитання',
            'Розбір вашої конкретної ситуації',
          ],
        },
      ],
    },
    pricing: {
      eyebrow: 'Тарифи',
      title: 'Оберіть свій формат',
      subtitle:
        'Для отримання безкоштовного вступного уроку — залиште заявку у формі нижче',
      popular_badge: 'Найпопулярніший',
      plans: [
        {
          name: 'Стандартний',
          price: '$300',
          features: [
            'Старт протягом доби після оплати',
            'Два відео-уроки на тиждень',
            '5 відео-уроків (перегляд у зручний час)',
            'Домашні завдання без перевірки',
            'Додаткові матеріали',
            'Доступ до матеріалів на 1 місяць',
          ],
          cta: 'Обрати тариф',
        },
        {
          name: 'Просунутий',
          price: '$550',
          features: [
            'Старт протягом доби після оплати',
            'Два відео-уроки на тиждень',
            '5 відео-уроків (перегляд у зручний час)',
            'Домашні завдання з перевіркою (протягом 3 днів)',
            'Додаткові матеріали',
            'Індивідуальна відео-зустріч з автором (~1 година)',
            'Доступ до матеріалів на 2 місяці',
          ],
          cta: 'Обрати тариф',
        },
        {
          name: 'Преміум',
          price: '$950',
          features: [
            'Старт протягом доби після оплати',
            'Індивідуальний графік',
            '5 відео-уроків (перегляд у зручний час)',
            'Домашні завдання з перевіркою',
            'Додаткові матеріали',
            '5 фідбек-сесій з автором курсу',
            'Доступ до матеріалів на 3 місяці',
          ],
          cta: 'Обрати тариф',
        },
      ],
    },
    communities: {
      eyebrow: 'Спільноти',
      title: 'Міжнародні чати вакансій від ESCA',
      subtitle:
        'Приєднуйтесь до наших Telegram-спільнот з актуальними вакансіями',
      items: [
        { name: 'Executive Search UA', desc: 'Дохід від $2 000' },
        { name: 'Premium Job UA', desc: 'Дохід від $5 000' },
        { name: 'IT Job UA', desc: 'IT вакансії' },
      ],
    },
    ctaBanner: {
      title: 'Отримайте вступний урок безкоштовно',
      subtitle:
        "Залиште заявку — і ми надішлемо перший урок курсу «Кар'єра» та відповімо на ваші запитання.",
      button: 'Залишити заявку',
    },
    faqItems: [
      {
        q: 'Для кого цей курс?',
        a: 'Для спеціалістів і керівників, які хочуть перейти на наступний рівень: змінити компанію, вирости до керівної позиції або суттєво підняти дохід. Курс не для тих, хто шукає першу роботу.',
      },
      {
        q: 'Скільки часу займає проходження?',
        a: 'Курс складається з відеоуроків, які ви проходите у своєму темпі. Більшість завершує його за 3–4 тижні, приділяючи 2–3 години на тиждень.',
      },
      {
        q: 'Чим це відрізняється від безкоштовних порад в інтернеті?',
        a: 'Курс побудований з боку того, хто 20 років приймає рішення про найм. Ви побачите процес відбору очима роботодавця й рекрутера: за що реально відмовляють, як читають резюме та що вирішує на переговорах про зарплату.',
      },
      {
        q: 'Чи є зворотний звʼязок від автора?',
        a: 'Так, на тарифах із супроводом. Ви отримуєте перевірку домашніх завдань і фідбек-сесії особисто з Сергієм Денисюком. Кількість сесій залежить від обраного тарифу.',
      },
      {
        q: 'Чи гарантуєте ви працевлаштування?',
        a: 'Ні, і жоден чесний курс цього не гарантує — фінальне рішення завжди за роботодавцем. Ми даємо інструменти, які суттєво підвищують кількість запрошень на співбесіди та якість офферів.',
      },
      {
        q: 'Можна спочатку подивитись безкоштовно?',
        a: 'Так. Залиште заявку — і ми надішлемо вступний урок безкоштовно. Так ви зрозумієте формат і подачу до оплати.',
      },
    ],
  },
}

;(UA as Translations).hero_form = {
  title: 'Знайдемо потрібного спеціаліста',
  subtitle: 'Залиште заявку — зв’яжемося протягом 15 хвилин',
  name_label: 'Як до Вас звертатись?',
  name_placeholder: "Ваше ім'я",
  phone_label: 'Телефон',
  position_label: 'Кого ви шукаєте?',
  position_placeholder: 'Виберіть посаду',
  positions: [
    'CEO / Генеральний директор',
    'COO / Операційний директор',
    'CFO / Фінансовий директор',
    'CTO / Технічний директор',
    'CMO / Директор з маркетингу',
    'CHRO / HR директор',
    'Керівник відділу продажів',
    'Менеджер з продажів',
    'Маркетолог',
    'Розробник (Frontend / Backend / Fullstack)',
    'DevOps / SRE інженер',
    'QA інженер',
    'Product Manager',
    'UX/UI Designer',
    'Фінансист / Бухгалтер',
    'Юрист',
    'HR / Рекрутер',
    'Команда під ключ',
    'Інша позиція',
  ],
  submit: 'Знайти',
  submitting: 'Надсилання...',
  success_title: 'Заявку надіслано!',
  success_subtitle: "Ми зв'яжемося з вами протягом 15 хвилин",
  privacy: 'Натискаючи «Знайти», ви погоджуєтесь з політикою конфіденційності',
  errors: {
    name: "Введіть ім'я",
    phone_required: 'Введіть телефон',
    phone_invalid: 'Некоректний номер',
    position: 'Оберіть позицію',
  },
}

const RU: Translations = {
  nav: {
    home: 'Главная',
    about: 'О компании',
    services: 'Услуги',
    employers: 'Работодателям',
    blog: 'Блог',
    contacts: 'Контакты',
  },
  header: {
    cta: 'Оставить заявку',
  },
  hero: {
    badge: 'Более 20 лет на рынке рекрутинга',
    h1_1: 'Рекрутинговое агентство по подбору персонала',
    h1_accent: '',
    h1_2: '',
    subtitle: 'Поиск и подбор опытных специалистов для вашей компании',
    cta_primary: 'Заказать подбор персонала',
    cta_secondary: 'Наши отрасли и специализации',
    stat1_val: '3 500+',
    stat1_label: 'закрытых вакансий',
    stat2_val: '98%',
    stat2_label: 'успешность подбора',
    stat3_val: '500+',
    stat3_label: 'клиентов',
    trust: 'компаний доверяют нам',
  },
  achievements: {
    stats: [
      {
        label: 'Лет на рынке',
        description:
          'Работаем с 2013 года, накопили экспертизу в десятках отраслей',
        suffix: '+',
      },
      {
        label: 'Закрытых вакансий',
        description:
          'От линейных специалистов до C‑level позиций в разных индустриях',
        suffix: '+',
      },
      {
        label: 'Клиентов',
        description:
          'Компании от стартапов до международных корпораций доверяют нам подбор',
        suffix: '+',
      },
      {
        label: 'Успешность подбора',
        description:
          'Кандидаты успешно остаются в команде после испытательного срока',
        suffix: '%',
      },
      {
        label: 'Средний срок закрытия',
        description:
          'Быстрый подбор без потери качества — экономим ваше время и ресурсы',
        suffix: ' дней',
      },
      {
        label: 'Гарантия замены',
        description:
          'Бесплатная замена кандидата, если он не подошёл в течение испытательного срока',
        suffix: '%',
      },
    ],
  },
  services: {
    eyebrow: 'Что мы делаем',
    title: 'Наши услуги',
    subtitle:
      'Специализируемся на поиске ключевых специалистов и построении эффективных команд для вашего бизнеса',
    badge: 'Ключевая специализация',
    card1_title: 'Executive Search (C‑level)',
    card1_desc:
      'Поиск и подбор топ‑менеджеров: CEO, COO, CFO, CTO, CMO и других руководителей высшего звена. Глубокое понимание рынка и конфиденциальный подход к каждому проекту.',
    card1_stat1_val: '14',
    card1_stat1_label: 'дней в среднем',
    card1_stat2_val: '100%',
    card1_stat2_label: 'гарантия замены',
    card2_title: 'Подбор IT‑специалистов',
    card2_desc:
      'Разработчики, DevOps, QA, Product Manager, дизайнеры — знаем специфику IT‑рынка и говорим на одном языке с кандидатами.',
    card3_title: 'Комплектация команды',
    card3_desc:
      'Формирование целых отделов и команд под ключ — от определения структуры до выхода последнего специалиста.',
    card3_case_val: '6 недель',
    card3_case_label: 'Собрали IT‑отдел из 15 человек',
    card4_title: 'Карьерные консультации',
    card4_desc:
      'Оценка карьерных возможностей, подготовка к собеседованиям, стратегия развития и позиционирование на рынке.',
    card4_count: '200+ консультаций',
  },
  niches: {
    eyebrow: 'Специализация',
    title: 'Ниши и направления',
    subtitle:
      'Подбираем специалистов в ключевых отраслях — от линейных позиций до топ‑менеджмента',
    note: 'Работаем со всеми офисными и управленческими позициями. Не специализируемся на рабочих специальностях и массмаркете.',
    positions_label: 'позиций',
    other_positions:
      'И другие позиции в этом направлении — обратитесь, и мы подберём нужного специалиста',
    categories: [
      { title: 'C‑level & Executive' },
      { title: 'IT & Digital' },
      { title: 'Финансы и банкинг' },
      { title: 'Продажи и бизнес‑развитие' },
      { title: 'Маркетинг и PR' },
      { title: 'HR & People' },
      { title: 'Логистика и операции' },
      { title: 'Юриспруденция' },
      { title: 'E‑commerce & Retail' },
      { title: 'Администрация и поддержка' },
    ],
  },
  process: {
    eyebrow: 'Как мы работаем',
    title: 'Этапы подбора',
    subtitle:
      'Прозрачный процесс от первого звонка до выхода кандидата на работу',
    steps: [
      {
        title: 'Бриф',
        description:
          'Изучаем потребности компании, формируем профиль идеального кандидата',
      },
      {
        title: 'Вакансия',
        description: 'Составляем привлекательное описание и стратегию поиска',
      },
      {
        title: 'Поиск',
        description:
          'Активный хантинг по базе 20 000+ кандидатов и открытым источникам',
      },
      {
        title: 'Отбор',
        description: 'Проводим скрининг, оцениваем компетенции и мотивацию',
      },
      {
        title: 'Интервью',
        description:
          'Организуем встречи с финалистами, собираем обратную связь',
      },
      {
        title: 'Оффер',
        description:
          'Помогаем согласовать условия и сопровождаем выход на работу',
      },
    ],
  },
  advantages: {
    eyebrow: 'Почему мы',
    title: 'Наши преимущества',
    subtitle:
      'Мы не просто закрываем вакансии — мы строим команды, которые двигают бизнес вперёд',
    stat1_label:
      'кандидатов успешно остаются в команде после испытательного срока',
    stat2_val: '4.9',
    stat2_label: 'Рейтинг на Clutch',
    stat3_val: '14 дней',
    stat3_label: 'Средний time‑to‑hire',
    items: [
      {
        title: 'Широкая база кандидатов',
        description:
          'Более 20 000+ проверенных специалистов в нашей базе данных по всем направлениям',
      },
      {
        title: 'Персональный подход',
        description:
          'Персональный рекрутер для каждого проекта, глубокое погружение в специфику бизнеса',
      },
      {
        title: 'Экономия времени и затрат',
        description:
          'Средний срок закрытия — 14 дней. Вы экономите ресурсы на поиск и отбор',
      },
      {
        title: 'Прозрачные цены',
        description:
          'Фиксированная стоимость без скрытых платежей. Оплата по факту выхода кандидата',
      },
      {
        title: 'Гарантия замены',
        description:
          'Если кандидат не подошёл в течение испытательного срока — подберём замену бесплатно',
      },
      {
        title: 'Аналитика и отчётность',
        description:
          'Еженедельные отчёты о ходе подбора, аналитика рынка и рекомендации по условиям',
      },
    ],
  },
  ceo: {
    eyebrow: 'Основатель агентства',
    role: 'CEO & Founder ESCA',
    years_label: 'лет на рынке',
    quote:
      '«Мы знаем всё о болях компаний по формированию кадрового состава, находясь непосредственно в реальном операционном бизнесе. Наша задача — быстро и качественно решить ваш вопрос и найти специалиста, максимально соответствующего вашей вакансии!»',
    points: [
      {
        title: 'Реальный бизнес‑опыт.',
        description:
          'Понимание потребностей компаний изнутри — не теоретически, а из практики управления операционным бизнесом.',
      },
      {
        title: 'Экспертиза Executive Search.',
        description:
          'Специализация на поиске C‑level и ключевых специалистов для бизнеса разных масштабов.',
      },
      {
        title: 'Личная вовлечённость.',
        description:
          'Лично контролирует ключевые проекты и гарантирует качество подбора на каждом этапе.',
      },
    ],
  },
  testimonials: {
    eyebrow: 'Отзывы',
    title: 'Нам доверяют',
    items: [
      {
        quote:
          'Самое впечатляющее и уникальное в этой компании — очень хорошие коммуникативные навыки, их понимание вакансий, подход к поиску кандидатов. Работать с ESCA было огромным удовольствием, и мы будем рады сотрудничать и в будущем. Никогда не останавливайтесь и развивайтесь!',
        role: 'Founder, Code-Smith',
      },
      {
        quote:
          'Мы выбрали ESCA среди нескольких рекрутинговых компаний, которые нашли в интернете. Решающими факторами для нас стали готовность закрыть все наши открытые позиции и наиболее подходящая ценовая модель. Подрядчики, которых ESCA помогла нам найти, были из разных областей, но в основном из IT/разработки. Подавляющее большинство людей, которых мы нашли через ESCA, успешно продолжают карьеру в нашей компании. Среди них — Support Agents, DevOps-инженеры (k8s и GCP), Python-разработчики, PHP-разработчики, Front-end разработчики (React).',
        role: 'CTO Representative, SaaS Company',
      },
      {
        quote:
          'Сергей и его команда — компания ESCA подобрали разработчиков для нашей компании. Проект был завершён вовремя и в рамках согласованного объёма. Работа была выполнена качественно. Мы остались очень довольны результатом. Рекомендуем как ответственное рекрутинговое агентство.',
        role: 'CEO, Dizair',
      },
      {
        quote:
          'ESCA — это очень ответственные партнёры с высоким уровнем выполнения задач. Мы постоянно развиваемся и нанимаем новых разработчиков. ESCA помогает нам уже 1,5 года.',
        role: 'PM, Andromo LTD',
      },
      {
        quote:
          'Мы воспользовались услугами ESCA для найма HRD и CFO. Мы довольны всем процессом сотрудничества.',
        role: 'Managing Partner, SBSB Fintech Lawyers',
      },
      {
        quote:
          'В процессе работы и общения были учтены все наши требования к кандидатам. Менеджер всегда отвечал на наши сообщения в течение 5-10 минут и всегда был на связи по телефону. Я полностью доволен результатами работы! Желаю ESCA дальнейшего роста и развития!',
        role: 'Co-Founder, para.agency',
      },
    ],
  },
  clients: {
    eyebrow: 'Нам доверяют',
    title: 'Компании, для которых мы закрывали позиции',
  },
  faq: {
    eyebrow: 'Вопросы и ответы',
    title: 'Частые вопросы',
    subtitle:
      'Отвечаем на то, что клиенты спрашивают чаще всего перед стартом сотрудничества',
    items: [
      {
        q: 'Сколько стоят ваши услуги?',
        a: 'Стоимость зависит от уровня позиции и сложности поиска. Для линейных и IT-специалистов мы работаем по фиксированной ставке, для C-level — по проценту от годового дохода кандидата. Точную стоимость называем после брифа: он бесплатный и ни к чему не обязывает.',
      },
      {
        q: 'Что будет, если кандидат не пройдёт испытательный срок?',
        a: 'Мы предоставляем гарантию замены. Если кандидат увольняется или не проходит испытательный срок, мы бесплатно ищем замену на эту же позицию. Срок гарантии фиксируется в договоре до старта работы.',
      },
      {
        q: 'Сколько времени занимает закрытие вакансии?',
        a: 'В среднем 14 дней. Первых релевантных кандидатов вы получаете уже через 3–5 рабочих дней после утверждения профиля. Сложные C-level и редкие технические позиции могут занимать до 4–6 недель.',
      },
      {
        q: 'Вы работаете с моей отраслью?',
        a: 'Мы закрываем позиции в 10 направлениях: C-level, IT и Digital, финансы, продажи, маркетинг, HR, логистика, юриспруденция, e-commerce и администрация. За 20 лет мы работали с более чем 500 компаниями — от стартапов до международных групп.',
      },
      {
        q: 'Что если вы не найдёте кандидата?',
        a: 'Мы работаем по предоплатной или поэтапной модели в зависимости от позиции. Если поиск не дал результата, вы не платите финальную часть вознаграждения. Условия всегда прописаны в договоре до старта.',
      },
      {
        q: 'Как вы ищете кандидатов?',
        a: 'Используем собственную базу из 20 000+ контактов, прямой хедхантинг, профессиональные сообщества и рекомендации. Мы не ограничиваемся откликами с job-бордов — большинство сильных кандидатов не ищет работу активно.',
      },
      {
        q: 'Нужно ли подписывать договор?',
        a: 'Да. Мы работаем официально по договору, в котором фиксируются стоимость, сроки, гарантия замены и условия конфиденциальности. Для чувствительных поисков подписываем NDA.',
      },
      {
        q: 'С чего начать сотрудничество?',
        a: 'Оставьте заявку — мы свяжемся в течение 15 минут, проведём бриф на 30 минут и отправим коммерческое предложение со сроками и стоимостью. Бриф бесплатный.',
      },
    ],
  },
  contact: {
    eyebrow: 'Свяжитесь с нами',
    title: 'Оставить заявку',
    subtitle:
      'Расскажите о вашей задаче — мы свяжемся в течение 15 минут и предложим решение',
    phone_label: 'Телефон',
    email_label: 'E‑mail',
    messenger_label: 'Или напишите нам в мессенджер:',
    trust_badge: 'Отвечаем в течение 15 минут в рабочее время',
    form_title: 'Заполните форму',
    name_label: 'Имя *',
    name_placeholder: 'Ваше имя',
    company_label: 'Компания *',
    company_placeholder: 'Название компании',
    position_label: 'Кого вы ищете? *',
    position_placeholder:
      'Например: CTO, руководитель отдела продаж, Senior Java Developer',
    phone_form_label: 'Телефон *',
    phone_placeholder: '+38 (0XX) XXX‑XX‑XX',
    email_form_label: 'E‑mail (необязательно)',
    email_placeholder: 'your@email.com',
    submit: 'Отправить заявку',
    submitting: 'Отправка...',
    success_title: 'Заявка отправлена!',
    success_subtitle: 'Мы свяжемся с вами в течение 15 минут в рабочее время',
    send_another: 'Отправить ещё одну заявку',
    privacy:
      'Нажимая «Отправить», вы соглашаетесь с политикой конфиденциальности',
    errors: {
      name: 'Введите имя',
      company: 'Введите название компании',
      position: 'Укажите, кого вы ищете',
      phone_required: 'Введите телефон',
      phone_invalid: 'Некорректный номер',
      email_required: 'Введите e‑mail',
      email_invalid: 'Некорректный e‑mail',
    },
  },
  footer: {
    description:
      'Executive Search Consulting Agency. Помогаем бизнесу находить лучших специалистов, а профессионалам — работу мечты.',
    address: '04053, г. Киев, ул. Кудрявская 3/5',
    col1_title: 'Компания',
    col1_links: ['О нас', 'Услуги', 'Контакты'],
    col2_title: 'Услуги',
    col2_links: [
      'Executive Search (C‑level)',
      'Подбор IT‑специалистов',
      'Комплектация команды',
      'Курс «Карьера»',
    ],
    copyright: '© 2026 ESCA. Все права защищены.',
  },
  blog: {
    eyebrow: 'Блог',
    title: 'Статьи и кейсы',
    all_articles: 'Все статьи',
    read_more: 'Читать далее',
  },
  executiveSearch: {
    title: 'Executive Search',
    badge: 'Ключевая специализация',
    h1_1: 'Executive Search',
    h1_accent: '(C‑level)',
    subtitle:
      'Поиск и подбор топ‑менеджеров: CEO, COO, CFO, CTO, CMO и других руководителей высшего звена. Глубокое понимание рынка и конфиденциальный подход к каждому проекту.',
    cta_primary: 'Оставить заявку',
    cta_secondary: 'Как мы работаем',
    stats: {
      days_val: '14',
      days_label: 'дней в среднем',
      guarantee_val: '100%',
      guarantee_label: 'гарантия замены',
      closed_val: '500+',
      closed_label: 'C‑level закрыто',
      success_val: '98%',
      success_label: 'успешность подбора',
    },
    whatWeDo: {
      eyebrow: 'Что входит в услугу',
      title: 'Полный цикл поиска топ‑менеджеров',
      subtitle:
        'От формирования профиля до выхода кандидата — мы сопровождаем весь процесс',
      items: [
        {
          title: 'Широкий поиск кандидатов',
          desc: 'Используем все доступные источники: собственные 5 сообществ и 4 группы топ-менеджеров, открытые площадки, LinkedIn и прямой хантинг (по договоренности).',
        },
        {
          title: 'Глубокое понимание рынка',
          desc: 'Знаем рынок топ-менеджеров в ключевых отраслях — кто есть, где работает, какая репутация. Это позволяет быстро выходить на нужных кандидатов без лишних затрат времени.',
        },
        {
          title: 'Проверка референсов',
          desc: 'Сбор отзывов от бывших работодателей и коллег — доступно как дополнительная опция по отдельной договоренности.',
          badge: 'Опционально',
        },
        {
          title: 'Оценка соответствия',
          desc: 'Проводим интервью с кандидатами, оцениваем соответствие требованиям позиции, опыт и мотивацию. Передаем клиенту тех, кто реально подходит.',
        },
        {
          title: 'Переговоры и оффер',
          desc: 'Помогаем структурировать компенсационный пакет, ведем переговоры и сопровождаем подписание контракта.',
        },
        {
          title: 'Гарантия замены',
          desc: 'Если кандидат не подошел в течение испытательного срока — подберем замену бесплатно. Без лишних вопросов.',
        },
      ],
    },
    positions: {
      eyebrow: 'Позиции',
      title: 'Каких руководителей мы подбираем',
      subtitle:
        'Специализируемся на поиске топ‑менеджеров для компаний любого масштаба — от стартапов до международных корпораций.',
      note: 'Средний опыт наших кандидатов — 15+ лет в отрасли. Мы не подбираем «перспективных» — мы находим тех, кто уже доказал свою эффективность.',
      items: [
        'CEO / Генеральный директор',
        'COO / Операционный директор',
        'CFO / Финансовый директор',
        'CTO / Технический директор',
        'CMO / Директор по маркетингу',
        'CHRO / HR директор',
        'CSO / Директор по продажам',
        'CDO / Директор по цифровой трансформации',
        'Board Member / Член совета директоров',
        'Country Manager',
        'VP of Engineering',
        'VP of Product',
      ],
      other:
        'И другие позиции в этом направлении — обратитесь, и мы подберем нужного специалиста',
    },
    process: {
      eyebrow: 'Процесс',
      title: 'Как мы находим топ‑менеджеров',
      steps: [
        {
          title: 'Стратегический бриф',
          desc: 'Глубокое погружение в бизнес клиента. Изучаем стратегию, культуру, команду и ожидания от нового руководителя.',
        },
        {
          title: 'Формирование лонг‑листа',
          desc: 'Определяем приоритетные источники поиска, формируем список потенциальных кандидатов с обоснованием каждого.',
        },
        {
          title: 'Поиск и хантинг',
          desc: 'Активный поиск через собственные сообщества, открытые источники и прямой выход на целевых кандидатов.',
        },
        {
          title: 'Интервью и отбор',
          desc: 'Проводим интервью с кандидатами, оцениваем соответствие позиции и мотивацию. Отбираем лучших.',
        },
        {
          title: 'Презентация кандидатов',
          desc: 'Представляем финалистов клиенту с детальными профилями. Организуем встречи и собираем обратную связь.',
        },
      ],
    },
    ctaBanner: {
      title: 'Нужен C‑level руководитель?',
      subtitle:
        'Расскажите о вашей задаче — мы свяжемся в течение 15 минут и предложим план действий.',
      button: 'Оставить заявку',
    },
    formPositions: [
      'CEO / Генеральный директор',
      'COO / Операционный директор',
      'CFO / Финансовый директор',
      'CTO / Технический директор',
      'CMO / Директор по маркетингу',
      'CHRO / HR директор',
      'CSO / Директор по продажам',
      'CDO / Директор по цифровой трансформации',
      'VP of Engineering',
      'VP of Product',
      'Country Manager',
      'Board Member / Советник',
      'Другая C‑level позиция',
    ],
    faqItems: [
      {
        q: 'Чем Executive Search отличается от обычного рекрутинга?',
        a: 'Мы не публикуем вакансию и не ждём откликов. Сильные руководители не ищут работу активно — мы составляем карту рынка, выходим на них напрямую и ведём переговоры конфиденциально. Это прямой целевой поиск, а не работа с входящим потоком.',
      },
      {
        q: 'Сколько стоит подбор руководителя?',
        a: 'Для C-level мы работаем по проценту от годового дохода кандидата. Точная ставка зависит от уровня позиции, отрасли и географии поиска. Стоимость фиксируем в договоре после брифа — он бесплатный.',
      },
      {
        q: 'Сколько времени занимает поиск топ-менеджера?',
        a: 'От 3 до 6 недель в зависимости от уровня позиции. Первый лонглист по карте рынка вы получаете в течение 7–10 дней, шортлист из 3–5 финалистов — к концу третьей недели.',
      },
      {
        q: 'Гарантируете ли вы результат?',
        a: 'Да. Мы предоставляем гарантию замены: если руководитель покидает компанию в течение гарантийного периода, мы проводим повторный поиск бесплатно. Срок гарантии для C-level фиксируется в договоре.',
      },
      {
        q: 'Можно ли сохранить поиск в тайне от рынка?',
        a: 'Да, это стандартная практика для Executive Search. Мы подписываем NDA и ведём поиск без раскрытия названия компании до этапа финальных переговоров. Это критично, если позиция ещё занята.',
      },
      {
        q: 'Как вы оцениваете кандидатов уровня C-level?',
        a: 'Кроме глубинного интервью, мы проверяем реальные бизнес-результаты кандидата, собираем рекомендации от бывших руководителей и партнёров, оцениваем соответствие корпоративной культуре. Вы получаете развёрнутый отчёт по каждому финалисту.',
      },
    ],
  },
  itRecruitment: {
    title: 'Подбор IT‑специалистов',
    badge: 'IT Recruitment',
    h1_1: 'Подбор',
    h1_accent: 'IT‑специалистов',
    subtitle:
      'Разработчики, DevOps, QA, Product Manager, дизайнеры — знаем специфику IT‑рынка и говорим на одном языке с кандидатами.',
    cta_primary: 'Оставить заявку',
    cta_secondary: 'Наши стеки',
    stats: {
      days_val: '14',
      days_label: 'дней в среднем',
      stacks_val: '50+',
      stacks_label: 'технологических стеков',
      closed_val: '1500+',
      closed_label: 'IT‑вакансий закрыто',
      success_val: '98%',
      success_label: 'успешность подбора',
    },
    stacks: {
      eyebrow: 'Технологии',
      title: 'Стеки и специализации',
      subtitle: 'Подбираем специалистов любого уровня — от junior до tech lead',
    },
    whyUs: {
      eyebrow: 'Почему мы',
      title: 'Мы понимаем IT изнутри',
      subtitle:
        'Наши рекрутеры имеют технический бэкграунд и прошли специализированное обучение. Мы не просто читаем резюме — мы понимаем, что стоит за каждой строкой.',
      points: [
        {
          title: 'Технический скрининг',
          desc: 'Проверяем реальные навыки, а не только список технологий в резюме',
        },
        {
          title: 'Активный хантинг',
          desc: 'Находим даже пассивных кандидатов, которые не ищут работу активно',
        },
        {
          title: 'Рыночная аналитика',
          desc: 'Предоставляем актуальные данные по зарплатам и рынку IT‑специалистов',
        },
        {
          title: 'Скорость',
          desc: 'Первые кандидаты — уже на 3–5 день после старта поиска',
        },
      ],
      cards: [
        {
          title: 'Быстрый старт',
          desc: 'Запускаем поиск в течение 24 часов после подписания договора',
        },
        {
          title: 'База 20 000+ кандидатов',
          desc: 'Собственная база проверенных IT‑специалистов, которая пополняется ежедневно',
        },
        {
          title: 'Прозрачная коммуникация',
          desc: 'Еженедельные отчеты, доступ к воронке кандидатов в реальном времени',
        },
      ],
    },
    ctaBanner: {
      title: 'Нужен IT‑специалист?',
      subtitle:
        'Опишите вашу вакансию — мы свяжемся в течение 15 минут и предложим план поиска.',
      button: 'Оставить заявку',
    },
    formPositions: [
      'JavaScript / TypeScript Developer',
      'React Developer',
      'Vue / Angular Developer',
      'Node.js Developer',
      'Python Developer',
      'Java Developer',
      'C# / .NET Developer',
      'PHP Developer',
      'Go Developer',
      'Ruby Developer',
      'C / C++ Developer',
      'Rust Developer',
      'Fullstack Developer',
      'iOS Developer (Swift)',
      'Android Developer (Kotlin / Java)',
      'React Native / Flutter Developer',
      'DevOps / SRE / Cloud Engineer',
      'QA Engineer (Manual / Automation)',
      'Data Engineer',
      'Data Scientist / ML Engineer',
      'AI / LLM Engineer',
      'Solution / Cloud Architect',
      'Cybersecurity Engineer',
      'DBA / Database Engineer',
      'System / Network администратор',
      'Embedded / IoT Developer',
      'Game Developer',
      'Blockchain / Web3 Developer',
      'UX/UI / Product Designer',
      'Product Manager',
      'Project / Delivery Manager',
      'Business Analyst',
      'Scrum Master / Agile Coach',
      'Tech Lead / Team Lead',
      'CTO / Head of Engineering / VP Engineering',
      'Другая IT‑позиция',
    ],
    faqItems: [
      {
        q: 'Разбираются ли ваши рекрутеры в технологиях?',
        a: 'Да. Наши IT-рекрутеры понимают разницу между стеками, читают технические требования и не отправляют Java-разработчика на вакансию JavaScript. При необходимости мы привлекаем технического эксперта для предварительного скрининга.',
      },
      {
        q: 'Сколько времени занимает закрытие IT-вакансии?',
        a: 'В среднем 14 дней. Первые релевантные кандидаты — через 3–5 рабочих дней. Редкие стеки (Rust, Scala, ML-инженеры сеньорного уровня) могут занимать 3–5 недель.',
      },
      {
        q: 'Вы ищете только в Украине?',
        a: 'Нет. Мы работаем с украинским рынком, польским, странами Балтии, а также с кандидатами с релокацией и в формате remote. География зависит от вашей модели найма.',
      },
      {
        q: 'Что если разработчик уйдёт через два месяца?',
        a: 'Действует гарантия замены. Если специалист увольняется в рамках гарантийного периода, мы находим замену бесплатно. Срок гарантии прописан в договоре.',
      },
      {
        q: 'Сколько стоит подбор IT-специалиста?',
        a: 'Для IT-позиций мы предлагаем фиксированную ставку или процент от годового дохода — в зависимости от объёма. При найме команды от 3 человек действуют лучшие условия. Стоимость озвучиваем после брифа.',
      },
      {
        q: 'Можете ли вы взять на себя техническое интервью?',
        a: 'Мы проводим полный скрининг: релевантность опыта, стек, английский, мотивация и ожидания по компенсации. Финальное техническое интервью обычно проводит ваша команда — но мы можем организовать внешнего технического интервьюера.',
      },
    ],
  },
  teamBuilding: {
    title: 'Комплектация команды',
    badge: 'Команды под ключ',
    h1_1: 'Комплектация',
    h1_accent: 'команды',
    subtitle:
      'Формирование целых отделов и команд под ключ — от определения структуры до выхода последнего специалиста на работу.',
    cta_primary: 'Оставить заявку',
    cta_secondary: 'Наши кейсы',
    stats: {
      time_val: '6',
      time_label: 'недель — команда из 15 человек',
      teams_val: '27+',
      teams_label: 'команд сформировано',
      guarantee_val: '100%',
      guarantee_label: 'гарантия замены',
      size_val: '3–50',
      size_label: 'человек в команде',
    },
    whatIsIncluded: {
      eyebrow: 'Что входит',
      title: 'Полный цикл формирования команды',
      subtitle:
        'От анализа потребностей до онбординга последнего специалиста — мы берем на себя весь процесс',
      items: [
        {
          title: 'Организационное проектирование',
          desc: 'Помогаем определить оптимальную структуру команды, роли и зоны ответственности еще до начала поиска.',
          points: [
            'Анализ бизнес‑потребностей и целей',
            'Разработка организационной структуры',
            'Определение приоритетности найма',
          ],
        },
        {
          title: 'Параллельный поиск',
          desc: 'Одновременно ведем поиск по всем позициям — это сокращает общий срок формирования команды на 40%.',
          points: [
            'Выделенная команда рекрутеров',
            'Специализация по направлениям',
            'Единый дедлайн для всех позиций',
          ],
        },
        {
          title: 'Координация процесса',
          desc: 'Один менеджер проекта координирует весь процесс. Вы получаете единую точку контакта и полную прозрачность.',
          points: [
            'Еженедельные статус‑встречи',
            'Дашборд прогресса в реальном времени',
            'Проактивная коммуникация',
          ],
        },
        {
          title: 'Гарантия результата',
          desc: 'Фиксируем дедлайны в договоре. Гарантия бесплатной замены для каждого члена команды.',
          points: [
            'Фиксированный дедлайн в договоре',
            '100% гарантия замены',
            'Поддержка после выхода команды',
          ],
        },
      ],
    },
    cases: {
      eyebrow: 'Кейсы',
      title: 'Команды, которые мы собрали',
      items: [
        {
          badge: 'Финтех',
          val: '15',
          val_label: 'специалистов за 6 недель',
          desc: 'IT‑отдел для финтех‑стартапа после раунда Series A. Frontend, Backend, DevOps, QA и Team Lead.',
          result: '✓ 100% прошли испытательный срок',
        },
        {
          badge: 'Ритейл',
          val: '8',
          val_label: 'менеджеров за 4 недели',
          desc: 'Отдел продаж для e‑commerce компании. Региональные менеджеры и руководитель отдела.',
          result: '✓ +35% к выручке за квартал',
        },
        {
          badge: 'Логистика',
          val: '12',
          val_label: 'операционных менеджеров за 5 недель',
          desc: 'Операционный отдел для логистической компании при масштабировании на новые регионы.',
          result: '✓ Выход на 3 новых рынка',
        },
      ],
    },
    ctaBanner: {
      title: 'Нужна команда под ключ?',
      subtitle:
        'Расскажите о вашей задаче — мы разработаем план формирования команды и предложим сроки.',
      button: 'Оставить заявку',
    },
    formPositions: [
      'IT‑команда (Frontend + Backend + QA + DevOps)',
      'Команда продаж',
      'Команда маркетинга',
      'Команда продукта (PM, Designer, BA)',
      'Финансовая команда',
      'HR‑команда',
      'Операционная команда / Бэк‑офис',
      'Customer Support команда',
      'Стартап с нуля (все ключевые роли)',
      'Другой состав команды',
    ],
    faqItems: [
      {
        q: 'Сколько времени занимает сбор команды под ключ?',
        a: 'Команду из 5–8 специалистов мы формируем за 4–8 недель в зависимости от сложности ролей. Мы не ищем всех последовательно — параллельно ведём несколько направлений, поэтому команда выходит на работу почти одновременно.',
      },
      {
        q: 'Чем это выгоднее, чем нанимать каждого отдельно?',
        a: 'Вы экономите на стоимости за позицию, получаете согласованную по уровню и культуре команду, а не набор случайных людей, и не тратите месяцы менеджерского времени на десятки интервью. При объёме от 3 позиций действуют специальные условия.',
      },
      {
        q: 'Вы помогаете выстроить структуру команды?',
        a: 'Да. Если у вас ещё нет чёткого понимания состава, мы на брифе поможем определить необходимые роли, уровни (junior/middle/senior) и порядок найма — исходя из ваших задач и бюджета.',
      },
      {
        q: 'Что если часть команды не приживётся?',
        a: 'Гарантия замены действует на каждую позицию отдельно. Если кто-то из команды увольняется в гарантийный период, мы находим замену бесплатно, не затрагивая условия по другим ролям.',
      },
      {
        q: 'Можно начать с части команды?',
        a: 'Да. Многие клиенты начинают с ключевых ролей (тимлид, senior-специалисты), а затем добирают остальных. Мы составляем поэтапный план найма под ваш темп роста и бюджет.',
      },
      {
        q: 'Сколько это стоит?',
        a: 'Стоимость рассчитывается под конкретный состав команды. Чем больше позиций — тем ниже ставка за каждую. Коммерческое предложение с фиксированной суммой и сроками отправляем после бесплатного брифа.',
      },
    ],
  },
  careerConsulting: {
    title: 'Курс «Карьера»',
    badge: 'Авторский онлайн-курс',
    author: 'Авторский онлайн-курс Сергея Денисюка',
    h1: 'КАРЬЕРА',
    h1_sub: 'Как достичь желаемой должности самым эффективным путем',
    subtitle:
      'CEO и владельца международного агентства по подбору руководителей ESCA. Никакой «воды» — только суть. Полная переупаковка за две недели.',
    cta_primary: 'Получить вводный урок бесплатно',
    cta_secondary: 'Тарифы',
    stats: {
      lessons_val: '5',
      lessons_label: 'видео-уроков',
      time_val: '2',
      time_label: 'недели до результата',
      exp_val: '20+',
      exp_label: 'лет опыта автора',
      price_val: 'от $300',
      price_label: 'стоимость курса',
    },
    whatYouGet: {
      eyebrow: 'Что входит в курс',
      title: 'Задача — полностью переупаковать вас за две недели',
      subtitle:
        'Никакой «воды». Только суть. По результатам прохождения у вас будет продающее резюме, стратегия достижения нужной позиции и понимание всех нюансов построения карьеры.',
      items: [
        '5 видео-уроков с возможностью просмотра в удобное время',
        'Домашние задания с проверкой (кроме тарифа «Стандартный»)',
        'Разбор реальных кейсов из практики',
        'Список сайтов и чатов вакансий топ-менеджеров, сайтов с уровнями доходов и отзывами о работодателях',
        'Список рекомендуемой литературы',
        'Видео-встреча с автором курса (кроме тарифа «Стандартный»). В тарифе «Премиум» — 5 фидбек-сессий',
        'Резюме участников курса попадают в базу ESCA — мы работаем с ними при поступлении релевантных запросов',
      ],
      free_lesson_title: 'Бесплатный вводный урок',
      free_lesson_desc:
        'Оставьте заявку в форме ниже — и мы пришлем вводный урок к курсу «Карьера»',
    },
    modules: {
      eyebrow: 'Программа',
      title: 'Структура курса',
      subtitle:
        '5 видео-уроков + видео-встреча с автором. Два видео-урока в неделю — старт в течение суток после оплаты.',
      items: [
        {
          num: '01',
          title:
            'Карьерная стратегия. Построение личного бренда. Тайм-менеджмент',
          topics: [
            'Определение точки старта и цели',
            'Типы карьеры и стратегия достижения цели',
            'Личное карьерное преимущество',
            'Построение личного бренда',
            'Искусство тайм-менеджмента',
          ],
        },
        {
          num: '02',
          title: 'Подготовка резюме. Создание продающего резюме',
          topics: [
            'Размер резюме и его формат',
            'Какая информация должна быть в резюме',
            'Как происходит отбор резюме',
            'Секреты и лайфхаки продающего резюме',
            'Примеры типичных ошибок в реальных резюме',
          ],
        },
        {
          num: '03',
          title: 'Поиск лучших вакансий. Смена сферы',
          topics: [
            'Пути поиска работы',
            'Использование LinkedIn и Facebook',
            'Нетворкинг для поиска работы',
            'Рекомендации по смене сферы',
            'Типичные ошибки при поиске',
          ],
          extra: 'Дополнительно: сайты и каналы вакансий',
        },
        {
          num: '04',
          title: 'Подготовка к интервью. Переговоры по доходу',
          topics: [
            'Как получить полную информацию о компании',
            'Подготовка самопрезентации и к возможным вопросам',
            'Отличия в интервью с агентствами, HR и руководством',
            'Нюансы онлайн-интервью',
            'Оценка стоимости на рынке и обсуждение зарплаты',
          ],
          extra:
            'Дополнительно: сайты по уровню доходов и отзывам о работодателях',
        },
        {
          num: '05',
          title: 'Испытательный срок. Карьерный рост. Найм vs бизнес',
          topics: [
            'Успешный старт и план на испытательный срок',
            'Что делать, если работа не соответствует ожиданиям',
            'Оценка перспектив роста в компании',
            'Работа по найму или собственный бизнес — что взвесить',
          ],
        },
        {
          num: '06',
          title: 'Видео-встреча с автором курса',
          topics: [
            'Индивидуальная сессия с Сергеем Денисюком',
            'Ответы на личные вопросы',
            'Разбор вашей конкретной ситуации',
          ],
        },
      ],
    },
    pricing: {
      eyebrow: 'Тарифы',
      title: 'Выберите свой формат',
      subtitle:
        'Для получения бесплатного вводного урока — оставьте заявку в форме ниже',
      popular_badge: 'Самый популярный',
      plans: [
        {
          name: 'Стандартный',
          price: '$300',
          features: [
            'Старт в течение суток после оплаты',
            'Два видео-урока в неделю',
            '5 видео-уроков (просмотр в удобное время)',
            'Домашние задания без проверки',
            'Дополнительные материалы',
            'Доступ к материалам на 1 месяц',
          ],
          cta: 'Выбрать тариф',
        },
        {
          name: 'Продвинутый',
          price: '$550',
          features: [
            'Старт в течение суток после оплаты',
            'Два видео-урока в неделю',
            '5 видео-уроков (просмотр в удобное время)',
            'Домашние задания с проверкой (в течение 3 дней)',
            'Дополнительные материалы',
            'Индивидуальная видео-встреча с автором (~1 час)',
            'Доступ к материалам на 2 месяца',
          ],
          cta: 'Выбрать тариф',
        },
        {
          name: 'Премиум',
          price: '$950',
          features: [
            'Старт в течение суток после оплаты',
            'Индивидуальный график',
            '5 видео-уроков (просмотр в удобное время)',
            'Домашние задания с проверкой',
            'Дополнительные материалы',
            '5 фидбек-сессий с автором курса',
            'Доступ к материалам на 3 месяца',
          ],
          cta: 'Выбрать тариф',
        },
      ],
    },
    communities: {
      eyebrow: 'Сообщества',
      title: 'Международные чаты вакансий от ESCA',
      subtitle:
        'Присоединяйтесь к нашим Telegram-сообществам с актуальными вакансиями',
      items: [
        { name: 'Executive Search UA', desc: 'Доход от $2 000' },
        { name: 'Premium Job UA', desc: 'Доход от $5 000' },
        { name: 'IT Job UA', desc: 'IT вакансии' },
      ],
    },
    ctaBanner: {
      title: 'Получите вводный урок бесплатно',
      subtitle:
        'Оставьте заявку — и мы пришлем первый урок курса «Карьера» и ответим на ваши вопросы.',
      button: 'Оставить заявку',
    },
    faqItems: [
      {
        q: 'Для кого этот курс?',
        a: 'Для специалистов и руководителей, которые хотят перейти на следующий уровень: сменить компанию, вырасти до руководящей позиции или существенно поднять доход. Курс не для тех, кто ищет первую работу.',
      },
      {
        q: 'Сколько времени занимает прохождение?',
        a: 'Курс состоит из видеоуроков, которые вы проходите в своём темпе. Большинство завершает его за 3–4 недели, уделяя 2–3 часа в неделю.',
      },
      {
        q: 'Чем это отличается от бесплатных советов в интернете?',
        a: 'Курс построен со стороны того, кто 20 лет принимает решения о найме. Вы увидите процесс отбора глазами работодателя и рекрутера: за что реально отказывают, как читают резюме и что решает на переговорах о зарплате.',
      },
      {
        q: 'Есть ли обратная связь от автора?',
        a: 'Да, на тарифах с сопровождением. Вы получаете проверку домашних заданий и фидбек-сессии лично с Сергеем Денисюком. Количество сессий зависит от выбранного тарифа.',
      },
      {
        q: 'Гарантируете ли вы трудоустройство?',
        a: 'Нет, и ни один честный курс этого не гарантирует — финальное решение всегда за работодателем. Мы даём инструменты, которые существенно повышают количество приглашений на собеседования и качество офферов.',
      },
      {
        q: 'Можно сначала посмотреть бесплатно?',
        a: 'Да. Оставьте заявку — и мы пришлём вводный урок бесплатно. Так вы поймёте формат и подачу до оплаты.',
      },
    ],
  },
}

;(RU as Translations).hero_form = {
  title: 'Найдём нужного специалиста',
  subtitle: 'Оставьте заявку — свяжемся в течение 15 минут',
  name_label: 'Как к Вам обращаться?',
  name_placeholder: 'Ваше имя',
  phone_label: 'Телефон',
  position_label: 'Кого вы ищете?',
  position_placeholder: 'Выберите должность',
  positions: [
    'CEO / Генеральный директор',
    'COO / Операционный директор',
    'CFO / Финансовый директор',
    'CTO / Технический директор',
    'CMO / Директор по маркетингу',
    'CHRO / HR директор',
    'Руководитель отдела продаж',
    'Менеджер по продажам',
    'Маркетолог',
    'Разработчик (Frontend / Backend / Fullstack)',
    'DevOps / SRE инженер',
    'QA инженер',
    'Product Manager',
    'UX/UI Designer',
    'Финансист / Бухгалтер',
    'Юрист',
    'HR / Рекрутер',
    'Команда под ключ',
    'Другая позиция',
  ],
  submit: 'Найти',
  submitting: 'Отправка...',
  success_title: 'Заявка отправлена!',
  success_subtitle: 'Мы свяжемся с вами в течение 15 минут',
  privacy: 'Нажимая «Найти», вы соглашаетесь с политикой конфиденциальности',
  errors: {
    name: 'Введите имя',
    phone_required: 'Введите телефон',
    phone_invalid: 'Некорректный номер',
    position: 'Выберите позицию',
  },
}

const EN: Translations = {
  nav: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    employers: 'For Employers',
    blog: 'Blog',
    contacts: 'Contact',
  },
  header: {
    cta: 'Get in Touch',
  },
  hero: {
    badge: 'Over 20 years in recruitment',
    h1_1: 'Recruitment agency for hiring talent',
    h1_accent: '',
    h1_2: '',
    subtitle:
      'Search and selection of experienced specialists for your company',
    cta_primary: 'Order Recruitment',
    cta_secondary: 'Our Industries & Specializations',
    stat1_val: '3,500+',
    stat1_label: 'positions filled',
    stat2_val: '98%',
    stat2_label: 'placement success rate',
    stat3_val: '500+',
    stat3_label: 'clients',
    trust: 'companies trust us',
  },
  achievements: {
    stats: [
      {
        label: 'Years on the market',
        description:
          'Operating since 2013, with expertise across dozens of industries',
        suffix: '+',
      },
      {
        label: 'Positions filled',
        description:
          'From junior specialists to C‑level executives across various industries',
        suffix: '+',
      },
      {
        label: 'Clients',
        description:
          'Companies from startups to international corporations trust us with their hiring',
        suffix: '+',
      },
      {
        label: 'Placement success rate',
        description:
          'Candidates successfully remain with the team after the probation period',
        suffix: '%',
      },
      {
        label: 'Average time to fill',
        description:
          'Fast recruitment without sacrificing quality — saving your time and resources',
        suffix: ' days',
      },
      {
        label: 'Replacement guarantee',
        description:
          "Free candidate replacement if they don't work out during the probation period",
        suffix: '%',
      },
    ],
  },
  services: {
    eyebrow: 'What we do',
    title: 'Our Services',
    subtitle:
      'We specialize in finding key talent and building effective teams for your business',
    badge: 'Core Specialization',
    card1_title: 'Executive Search (C‑level)',
    card1_desc:
      'Search and selection of top managers: CEO, COO, CFO, CTO, CMO and other senior executives. Deep market understanding and a confidential approach to every project.',
    card1_stat1_val: '14',
    card1_stat1_label: 'days on average',
    card1_stat2_val: '100%',
    card1_stat2_label: 'replacement guarantee',
    card2_title: 'IT Recruitment',
    card2_desc:
      'Developers, DevOps, QA, Product Managers, designers — we know the IT market inside out and speak the same language as candidates.',
    card3_title: 'Team Building',
    card3_desc:
      'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
    card3_case_val: '6 weeks',
    card3_case_label: 'Built an IT team of 15 people',
    card4_title: 'Career Consulting',
    card4_desc:
      'Career opportunity assessment, interview preparation, development strategy and market positioning.',
    card4_count: '200+ consultations',
  },
  niches: {
    eyebrow: 'Specialization',
    title: 'Industries & Directions',
    subtitle:
      'We recruit specialists across key industries — from junior roles to top management',
    note: 'We work with all office and management positions. We do not specialize in blue-collar or mass-market roles.',
    positions_label: 'positions',
    other_positions:
      "And other positions in this direction — contact us and we'll find the right specialist",
    categories: [
      { title: 'C‑level & Executive' },
      { title: 'IT & Digital' },
      { title: 'Finance & Banking' },
      { title: 'Sales & Business Development' },
      { title: 'Marketing & PR' },
      { title: 'HR & People' },
      { title: 'Logistics & Operations' },
      { title: 'Legal' },
      { title: 'E‑commerce & Retail' },
      { title: 'Administration & Support' },
    ],
  },
  process: {
    eyebrow: 'How we work',
    title: 'Recruitment Process',
    subtitle:
      "A transparent process from the first call to the candidate's first day",
    steps: [
      {
        title: 'Brief',
        description:
          "We study the company's needs and build the ideal candidate profile",
      },
      {
        title: 'Job Posting',
        description:
          'We craft an attractive job description and search strategy',
      },
      {
        title: 'Search',
        description:
          'Active headhunting from a database of 20,000+ candidates and open sources',
      },
      {
        title: 'Screening',
        description: 'We screen candidates, assess competencies and motivation',
      },
      {
        title: 'Interview',
        description: 'We organize meetings with finalists and collect feedback',
      },
      {
        title: 'Offer',
        description:
          'We help negotiate terms and support the onboarding process',
      },
    ],
  },
  advantages: {
    eyebrow: 'Why us',
    title: 'Our Advantages',
    subtitle:
      "We don't just fill vacancies — we build teams that drive business forward",
    stat1_label:
      'of candidates successfully stay with the team after the probation period',
    stat2_val: '4.9',
    stat2_label: 'Rating on Clutch',
    stat3_val: '14 days',
    stat3_label: 'Average time‑to‑hire',
    items: [
      {
        title: 'Extensive candidate database',
        description:
          'Over 20,000+ verified specialists in our database across all directions',
      },
      {
        title: 'Personal approach',
        description:
          'A dedicated recruiter for every project with deep immersion in your business specifics',
      },
      {
        title: 'Time and cost savings',
        description:
          'Average time to fill — 14 days. You save resources on search and selection',
      },
      {
        title: 'Transparent pricing',
        description:
          "Fixed cost with no hidden fees. Payment upon the candidate's start date",
      },
      {
        title: 'Replacement guarantee',
        description:
          "If the candidate doesn't work out during the probation period — we'll find a replacement for free",
      },
      {
        title: 'Analytics & reporting',
        description:
          'Weekly recruitment progress reports, market analytics and compensation recommendations',
      },
    ],
  },
  ceo: {
    eyebrow: 'Agency Founder',
    role: 'CEO & Founder ESCA',
    years_label: 'years on the market',
    quote:
      '"We know everything about the pain points companies face when building their teams, having been directly involved in real operational business. Our goal is to quickly and effectively solve your hiring challenge and find the specialist who is the perfect fit for your vacancy!"',
    points: [
      {
        title: 'Real business experience.',
        description:
          'Understanding company needs from the inside — not theoretically, but from hands-on operational management experience.',
      },
      {
        title: 'Executive Search expertise.',
        description:
          'Specialization in finding C‑level and key specialists for businesses of all sizes.',
      },
      {
        title: 'Personal involvement.',
        description:
          'Personally oversees key projects and guarantees recruitment quality at every stage.',
      },
    ],
  },
  testimonials: {
    eyebrow: 'Client Testimonials',
    title: 'Trusted by',
    items: [
      {
        quote:
          'The most impressive and unique thing about this company is their excellent communication skills, their understanding of vacancies, and their approach to finding candidates. Working with ESCA was a great pleasure, and we look forward to collaborating in the future. Never stop and keep growing!',
        role: 'Founder, Code-Smith',
      },
      {
        quote:
          'We chose ESCA among several recruiting companies we found online. The decisive factors were their readiness to close all our open positions and the most suitable pricing model. The contractors ESCA helped us find came from different fields, but mostly IT/development. The vast majority of people we found through ESCA are successfully continuing their careers at our company — Support Agents, DevOps engineers (k8s and GCP), Python developers, PHP developers, and Front-end developers (React).',
        role: 'CTO Representative, SaaS Company',
      },
      {
        quote:
          'Sergey and his team at ESCA sourced developers for our company. The project was completed on time and within the agreed scope. The work was done to a high standard. We were very satisfied with the result. We recommend them as a responsible recruitment agency.',
        role: 'CEO, Dizair',
      },
      {
        quote:
          'ESCA are highly responsible partners with a strong track record of delivery. We are constantly growing and hiring new developers. ESCA has been helping us for 1.5 years now.',
        role: 'PM, Andromo LTD',
      },
      {
        quote:
          'We used ESCA to hire our HRD and CFO. We are satisfied with the entire cooperation process.',
        role: 'Managing Partner, SBSB Fintech Lawyers',
      },
      {
        quote:
          'Throughout the process, all our candidate requirements were taken into account. The manager always replied to our messages within 5-10 minutes and was always reachable by phone. I am completely satisfied with the results! I wish ESCA continued growth and success!',
        role: 'Co-Founder, para.agency',
      },
    ],
  },
  clients: {
    eyebrow: 'Trusted by',
    title: 'Companies we have hired for',
  },
  faq: {
    eyebrow: 'Questions & Answers',
    title: 'Frequently asked questions',
    subtitle:
      'The things clients most often ask before starting a collaboration',
    items: [
      {
        q: 'How much do your services cost?',
        a: 'Pricing depends on the seniority of the role and the difficulty of the search. For specialist and IT roles we work on a fixed fee; for C-level we charge a percentage of the candidate\u2019s annual compensation. We quote an exact price after the briefing — which is free and carries no obligation.',
      },
      {
        q: 'What happens if the candidate fails probation?',
        a: 'We provide a replacement guarantee. If the candidate leaves or does not pass probation, we search for a replacement for the same role at no additional cost. The guarantee period is fixed in the contract before work begins.',
      },
      {
        q: 'How long does it take to fill a vacancy?',
        a: 'On average 14 days. You receive the first relevant candidates within 3–5 working days of approving the profile. Complex C-level and rare technical roles can take 4–6 weeks.',
      },
      {
        q: 'Do you work in my industry?',
        a: 'We fill roles across 10 areas: C-level, IT & Digital, finance, sales, marketing, HR, logistics, legal, e-commerce and administration. Over 20 years we have worked with more than 500 companies — from startups to international groups.',
      },
      {
        q: 'What if you do not find a candidate?',
        a: 'We work on a retainer or milestone model depending on the role. If the search produces no result, you do not pay the final portion of the fee. The terms are always set out in the contract before we start.',
      },
      {
        q: 'How do you source candidates?',
        a: 'We use our own database of 20,000+ contacts, direct headhunting, professional communities and referrals. We do not rely on job-board applications — most strong candidates are not actively looking.',
      },
      {
        q: 'Is a contract required?',
        a: 'Yes. We work formally under a contract covering fees, timelines, the replacement guarantee and confidentiality terms. For sensitive searches we sign an NDA.',
      },
      {
        q: 'How do we get started?',
        a: 'Leave a request — we respond within 15 minutes, run a 30-minute briefing and send a proposal with timelines and pricing. The briefing is free.',
      },
    ],
  },
  contact: {
    eyebrow: 'Get in touch',
    title: 'Leave a Request',
    subtitle:
      "Tell us about your challenge — we'll get back to you within 15 minutes and propose a solution",
    phone_label: 'Phone',
    email_label: 'E‑mail',
    messenger_label: 'Or message us on:',
    trust_badge: 'We respond within 15 minutes during business hours',
    form_title: 'Fill out the form',
    name_label: 'Name *',
    name_placeholder: 'Your name',
    company_label: 'Company *',
    company_placeholder: 'Company name',
    position_label: 'Who are you looking for? *',
    position_placeholder: 'e.g. CTO, Head of Sales, Senior Java Developer',
    phone_form_label: 'Phone *',
    phone_placeholder: '+38 (0XX) XXX‑XX‑XX',
    email_form_label: 'E‑mail (optional)',
    email_placeholder: 'your@email.com',
    submit: 'Send Request',
    submitting: 'Sending...',
    success_title: 'Request sent!',
    success_subtitle:
      "We'll get back to you within 15 minutes during business hours",
    send_another: 'Send another request',
    privacy: 'By clicking "Send", you agree to our privacy policy',
    errors: {
      name: 'Please enter your name',
      company: 'Please enter your company name',
      position: 'Please tell us who you are looking for',
      phone_required: 'Please enter your phone number',
      phone_invalid: 'Invalid phone number',
      email_required: 'Please enter your email',
      email_invalid: 'Invalid email address',
    },
  },
  footer: {
    description:
      'Executive Search Consulting Agency. We help businesses find the best talent and professionals find their dream job.',
    address: '04053, Kyiv, Kudriavska St. 3/5',
    col1_title: 'Company',
    col1_links: ['About us', 'Services', 'Contact'],
    col2_title: 'Services',
    col2_links: [
      'Executive Search (C‑level)',
      'IT Recruitment',
      'Team Building',
      'Career Course',
    ],
    copyright: '© 2026 ESCA. All rights reserved.',
  },
  blog: {
    eyebrow: 'Blog',
    title: 'Articles & Cases',
    all_articles: 'All articles',
    read_more: 'Read more',
  },
  executiveSearch: {
    title: 'Executive Search',
    badge: 'Core Specialization',
    h1_1: 'Executive Search',
    h1_accent: '(C‑level)',
    subtitle:
      'Search and selection of top managers: CEO, COO, CFO, CTO, CMO and other senior executives. Deep market understanding and a confidential approach to every project.',
    cta_primary: 'Leave a Request',
    cta_secondary: 'How we work',
    stats: {
      days_val: '14',
      days_label: 'days on average',
      guarantee_val: '100%',
      guarantee_label: 'replacement guarantee',
      closed_val: '500+',
      closed_label: 'C‑level closed',
      success_val: '98%',
      success_label: 'placement success rate',
    },
    whatWeDo: {
      eyebrow: 'What is included',
      title: 'Full cycle of top‑manager search',
      subtitle:
        'From profile formation to candidate onboarding — we accompany the entire process',
      items: [
        {
          title: 'Broad candidate search',
          desc: 'We use all available sources: our own 5 communities and 4 top-manager groups, open platforms, LinkedIn, and direct headhunting (by agreement).',
        },
        {
          title: 'Deep market understanding',
          desc: 'We know the top-manager market in key industries — who is there, where they work, what their reputation is. This allows us to quickly reach the right candidates without wasting time.',
        },
        {
          title: 'Reference checking',
          desc: 'Collecting feedback from former employers and colleagues — available as an additional option by separate agreement.',
          badge: 'Optional',
        },
        {
          title: 'Fit assessment',
          desc: 'We conduct interviews with candidates, assess their fit for the position requirements, experience, and motivation. We present only those who truly fit.',
        },
        {
          title: 'Negotiation and offer',
          desc: 'We help structure the compensation package, conduct negotiations, and support the contract signing.',
        },
        {
          title: 'Replacement guarantee',
          desc: 'If the candidate does not work out during the probation period — we will find a replacement for free. No questions asked.',
        },
      ],
    },
    positions: {
      eyebrow: 'Positions',
      title: 'What executives we recruit',
      subtitle:
        'We specialize in finding top‑managers for companies of any scale — from startups to international corporations.',
      note: 'The average experience of our candidates is 15+ years in the industry. We do not recruit "promising" ones — we find those who have already proven their effectiveness.',
      items: [
        'CEO / Chief Executive Officer',
        'COO / Chief Operating Officer',
        'CFO / Chief Financial Officer',
        'CTO / Chief Technology Officer',
        'CMO / Chief Marketing Officer',
        'CHRO / HR Director',
        'CSO / Chief Sales Officer',
        'CDO / Chief Digital Officer',
        'Board Member',
        'Country Manager',
        'VP of Engineering',
        'VP of Product',
      ],
      other:
        'And other positions in this direction — contact us, and we will find the right specialist',
    },
    process: {
      eyebrow: 'Process',
      title: 'How we find top‑managers',
      steps: [
        {
          title: 'Strategic brief',
          desc: "Deep dive into the client's business. We study the strategy, culture, team, and expectations from the new leader.",
        },
        {
          title: 'Long‑list formation',
          desc: 'We determine priority search sources, form a list of potential candidates with justification for each.',
        },
        {
          title: 'Search and headhunting',
          desc: 'Active search through our own communities, open sources, and direct outreach to target candidates.',
        },
        {
          title: 'Interview and selection',
          desc: 'We conduct interviews with candidates, assess their fit for the position and motivation. We select the best.',
        },
        {
          title: 'Candidate presentation',
          desc: 'We present finalists to the client with detailed profiles. We organize meetings and collect feedback.',
        },
      ],
    },
    ctaBanner: {
      title: 'Need a C‑level executive?',
      subtitle:
        'Tell us about your task — we will contact you within 15 minutes and propose an action plan.',
      button: 'Leave a Request',
    },
    formPositions: [
      'CEO / Chief Executive Officer',
      'COO / Chief Operating Officer',
      'CFO / Chief Financial Officer',
      'CTO / Chief Technology Officer',
      'CMO / Chief Marketing Officer',
      'CHRO / Chief HR Officer',
      'CSO / Chief Sales Officer',
      'CDO / Chief Digital Officer',
      'VP of Engineering',
      'VP of Product',
      'Country Manager',
      'Board Member / Advisor',
      'Other C‑level position',
    ],
    faqItems: [
      {
        q: 'How is Executive Search different from regular recruiting?',
        a: 'We do not post a vacancy and wait for applications. Strong executives are not actively looking — we build a market map, approach them directly and negotiate confidentially. This is targeted direct search, not inbound processing.',
      },
      {
        q: 'How much does an executive search cost?',
        a: 'For C-level we charge a percentage of the candidate\u2019s annual compensation. The exact rate depends on seniority, industry and search geography. We fix the price in the contract after a free briefing.',
      },
      {
        q: 'How long does an executive search take?',
        a: '3 to 6 weeks depending on the seniority of the role. You receive the first longlist from the market map within 7–10 days, and a shortlist of 3–5 finalists by the end of week three.',
      },
      {
        q: 'Do you guarantee the result?',
        a: 'Yes. We provide a replacement guarantee: if the executive leaves within the guarantee period, we run the search again at no cost. The C-level guarantee period is fixed in the contract.',
      },
      {
        q: 'Can the search be kept confidential?',
        a: 'Yes, this is standard practice in Executive Search. We sign an NDA and run the search without disclosing your company name until final negotiations — critical when the role is still occupied.',
      },
      {
        q: 'How do you assess C-level candidates?',
        a: 'Beyond in-depth interviews, we verify real business results, collect references from former managers and partners, and assess cultural fit. You receive a detailed report on every finalist.',
      },
    ],
  },
  itRecruitment: {
    title: 'IT Recruitment',
    badge: 'IT Recruitment',
    h1_1: 'Hire Top',
    h1_accent: 'Tech Talent',
    subtitle:
      'Developers, DevOps, QA, Product Managers, designers — we know the specifics of the IT market and speak the same language as candidates.',
    cta_primary: 'Leave a Request',
    cta_secondary: 'Our Stacks',
    stats: {
      days_val: '14',
      days_label: 'days on average',
      stacks_val: '50+',
      stacks_label: 'tech stacks',
      closed_val: '1500+',
      closed_label: 'IT vacancies closed',
      success_val: '98%',
      success_label: 'placement success rate',
    },
    stacks: {
      eyebrow: 'Technologies',
      title: 'Stacks and Specializations',
      subtitle:
        'We recruit specialists of any level — from junior to tech lead',
    },
    whyUs: {
      eyebrow: 'Why Us',
      title: 'We understand IT from the inside',
      subtitle:
        "Our recruiters have a technical background and have undergone specialized training. We don't just read resumes — we understand what's behind every line.",
      points: [
        {
          title: 'Technical screening',
          desc: 'We check real skills, not just a list of technologies in a resume',
        },
        {
          title: 'Active headhunting',
          desc: 'We find even passive candidates who are not actively looking for a job',
        },
        {
          title: 'Market analytics',
          desc: 'We provide up-to-date data on salaries and the IT specialist market',
        },
        {
          title: 'Speed',
          desc: 'First candidates — already on the 3rd–5th day after the search starts',
        },
      ],
      cards: [
        {
          title: 'Quick start',
          desc: 'We launch the search within 24 hours after signing the contract',
        },
        {
          title: 'Database of 20,000+ candidates',
          desc: 'Our own database of verified IT specialists, which is updated daily',
        },
        {
          title: 'Transparent communication',
          desc: 'Weekly reports, access to the candidate funnel in real time',
        },
      ],
    },
    ctaBanner: {
      title: 'Need an IT specialist?',
      subtitle:
        'Describe your vacancy — we will contact you within 15 minutes and propose a search plan.',
      button: 'Leave a Request',
    },
    formPositions: [
      'JavaScript / TypeScript Developer',
      'React Developer',
      'Vue / Angular Developer',
      'Node.js Developer',
      'Python Developer',
      'Java Developer',
      'C# / .NET Developer',
      'PHP Developer',
      'Go Developer',
      'Ruby Developer',
      'C / C++ Developer',
      'Rust Developer',
      'Fullstack Developer',
      'iOS Developer (Swift)',
      'Android Developer (Kotlin / Java)',
      'React Native / Flutter Developer',
      'DevOps / SRE / Cloud Engineer',
      'QA Engineer (Manual / Automation)',
      'Data Engineer',
      'Data Scientist / ML Engineer',
      'AI / LLM Engineer',
      'Solution / Cloud Architect',
      'Cybersecurity Engineer',
      'DBA / Database Engineer',
      'System / Network Administrator',
      'Embedded / IoT Developer',
      'Game Developer',
      'Blockchain / Web3 Developer',
      'UX/UI / Product Designer',
      'Product Manager',
      'Project / Delivery Manager',
      'Business Analyst',
      'Scrum Master / Agile Coach',
      'Tech Lead / Team Lead',
      'CTO / Head of Engineering / VP Engineering',
      'Other IT position',
    ],
    faqItems: [
      {
        q: 'Do your recruiters understand technology?',
        a: 'Yes. Our IT recruiters understand the difference between stacks, read technical requirements properly, and will not send a Java developer for a JavaScript role. When needed we bring in a technical expert for pre-screening.',
      },
      {
        q: 'How long does it take to fill an IT vacancy?',
        a: 'On average 14 days. First relevant candidates arrive within 3–5 working days. Rare stacks (Rust, Scala, senior ML engineers) can take 3–5 weeks.',
      },
      {
        q: 'Do you only search in Ukraine?',
        a: 'No. We work across the Ukrainian, Polish and Baltic markets, as well as with relocation and fully remote candidates. Geography depends on your hiring model.',
      },
      {
        q: 'What if the developer leaves after two months?',
        a: 'The replacement guarantee applies. If the specialist resigns within the guarantee period, we find a replacement at no cost. The guarantee term is stated in the contract.',
      },
      {
        q: 'How much does IT recruitment cost?',
        a: 'For IT roles we offer either a fixed fee or a percentage of annual compensation, depending on volume. Hiring a team of 3+ unlocks better terms. We quote after the briefing.',
      },
      {
        q: 'Can you handle the technical interview?',
        a: 'We run full screening: relevance of experience, stack, English level, motivation and compensation expectations. The final technical interview is usually run by your team — though we can arrange an external technical interviewer.',
      },
    ],
  },
  teamBuilding: {
    title: 'Team Building',
    badge: 'Turnkey Teams',
    h1_1: 'Team',
    h1_accent: 'Building',
    subtitle:
      'We fill vacancies of any complexity in an average of 14 days. Personal approach, transparent terms, and a candidate replacement guarantee.',
    cta_primary: 'Order Recruitment',
    cta_secondary: 'Our Industries & Specializations',
    stats: {
      time_val: '14',
      time_label: 'days on average',
      teams_val: '3,500+',
      teams_label: 'positions filled',
      guarantee_val: '100%',
      guarantee_label: 'replacement guarantee',
      size_val: '98%',
      size_label: 'placement success rate',
    },
    whatIsIncluded: {
      eyebrow: 'What we do',
      title: 'Our Services',
      subtitle:
        'We specialize in finding key talent and building effective teams for your business',
      items: [
        {
          title: 'Executive Search (C‑level)',
          desc: 'Search and selection of top managers: CEO, COO, CFO, CTO, CMO and other senior executives. Deep market understanding and a confidential approach to every project.',
          points: [
            'Team Building',
            'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
          ],
        },
        {
          title: 'IT Recruitment',
          desc: 'Developers, DevOps, QA, Product Managers, designers — we know the IT market inside out and speak the same language as candidates.',
          points: [
            'Team Building',
            'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
          ],
        },
        {
          title: 'Career Consulting',
          desc: 'Career opportunity assessment, interview preparation, development strategy and market positioning.',
          points: [
            'Team Building',
            'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
          ],
        },
      ],
    },
    cases: {
      eyebrow: 'Specialization',
      title: 'Industries & Directions',
      items: [
        {
          badge: 'Core Specialization',
          val: '3,500+',
          val_label: 'positions filled',
          desc: 'We recruit specialists across key industries — from junior roles to top management',
          result:
            'We work with all office and management positions. We do not specialize in blue-collar or mass-market roles.',
        },
        {
          badge: 'Core Specialization',
          val: '98%',
          val_label: 'placement success rate',
          desc: 'We recruit specialists across key industries — from junior roles to top management',
          result:
            'We work with all office and management positions. We do not specialize in blue-collar or mass-market roles.',
        },
        {
          badge: 'Core Specialization',
          val: '14',
          val_label: 'days on average',
          desc: 'We recruit specialists across key industries — from junior roles to top management',
          result:
            'We work with all office and management positions. We do not specialize in blue-collar or mass-market roles.',
        },
        {
          badge: 'Core Specialization',
          val: '100%',
          val_label: 'replacement guarantee',
          desc: 'We recruit specialists across key industries — from junior roles to top management',
          result:
            'We work with all office and management positions. We do not specialize in blue-collar or mass-market roles.',
        },
      ],
    },
    ctaBanner: {
      title: 'Leave a Request',
      subtitle:
        "Tell us about your challenge — we'll get back to you within 15 minutes and propose a solution",
      button: 'Send Request',
    },
    formPositions: [
      'IT team (Frontend + Backend + QA + DevOps)',
      'Sales team',
      'Marketing team',
      'Product team (PM, Designer, BA)',
      'Finance team',
      'HR team',
      'Operations / Back‑office team',
      'Customer Support team',
      'Startup from scratch (all key roles)',
      'Other team composition',
    ],
    faqItems: [
      {
        q: 'How long does it take to build a full team?',
        a: 'We assemble a team of 5–8 specialists in 4–8 weeks depending on role complexity. We do not search sequentially — several tracks run in parallel, so the team starts almost simultaneously.',
      },
      {
        q: 'Why is this better than hiring each person separately?',
        a: 'You pay less per position, get a team aligned on seniority and culture rather than a set of unrelated hires, and avoid months of management time spent on dozens of interviews. Volumes of 3+ roles unlock special terms.',
      },
      {
        q: 'Do you help design the team structure?',
        a: 'Yes. If you do not yet have a clear composition in mind, during the briefing we help define the required roles, seniority levels and hiring order based on your goals and budget.',
      },
      {
        q: 'What if part of the team does not work out?',
        a: 'The replacement guarantee applies to each role individually. If someone leaves within the guarantee period, we find a replacement at no cost without affecting the terms for the other roles.',
      },
      {
        q: 'Can we start with only part of the team?',
        a: 'Yes. Many clients start with the key roles (team lead, senior specialists) and add the rest later. We build a phased hiring plan matched to your growth rate and budget.',
      },
      {
        q: 'How much does it cost?',
        a: 'Pricing is calculated for the specific team composition. The more roles, the lower the rate per hire. We send a proposal with a fixed amount and timeline after a free briefing.',
      },
    ],
  },
  careerConsulting: {
    title: 'Career Consulting',
    badge: 'Core Specialization',
    author: 'Sergey',
    h1: 'Career Consulting',
    h1_sub:
      'Career opportunity assessment, interview preparation, development strategy and market positioning.',
    subtitle:
      'Career opportunity assessment, interview preparation, development strategy and market positioning.',
    cta_primary: 'Order Recruitment',
    cta_secondary: 'Our Industries & Specializations',
    stats: {
      lessons_val: '200+',
      lessons_label: 'consultations',
      time_val: '14',
      time_label: 'days on average',
      exp_val: '3,500+',
      exp_label: 'positions filled',
      price_val: '98%',
      price_label: 'placement success rate',
    },
    whatYouGet: {
      eyebrow: 'What we do',
      title: 'Our Services',
      subtitle:
        'We specialize in finding key talent and building effective teams for your business',
      items: [
        'Team Building',
        'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
      ],
      free_lesson_title: 'Career opportunity assessment',
      free_lesson_desc:
        'Career opportunity assessment, interview preparation, development strategy and market positioning.',
    },
    modules: {
      eyebrow: 'Specialization',
      title: 'Industries & Directions',
      subtitle:
        'We recruit specialists across key industries — from junior roles to top management',
      items: [
        {
          num: '1',
          title: 'Team Building',
          topics: [
            'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
          ],
        },
        {
          num: '2',
          title: 'IT Recruitment',
          topics: [
            'Developers, DevOps, QA, Product Managers, designers — we know the IT market inside out and speak the same language as candidates.',
          ],
        },
        {
          num: '3',
          title: 'Career Consulting',
          topics: [
            'Career opportunity assessment, interview preparation, development strategy and market positioning.',
          ],
        },
      ],
    },
    pricing: {
      eyebrow: 'Specialization',
      title: 'Industries & Directions',
      subtitle:
        'We recruit specialists across key industries — from junior roles to top management',
      popular_badge: 'Core Specialization',
      plans: [
        {
          name: 'Team Building',
          price: '3,500+',
          features: [
            'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
          ],
          cta: 'Order Recruitment',
        },
        {
          name: 'IT Recruitment',
          price: '98%',
          features: [
            'Developers, DevOps, QA, Product Managers, designers — we know the IT market inside out and speak the same language as candidates.',
          ],
          cta: 'Order Recruitment',
        },
        {
          name: 'Career Consulting',
          price: '14',
          features: [
            'Career opportunity assessment, interview preparation, development strategy and market positioning.',
          ],
          cta: 'Order Recruitment',
        },
      ],
    },
    communities: {
      eyebrow: 'Specialization',
      title: 'Industries & Directions',
      subtitle:
        'We recruit specialists across key industries — from junior roles to top management',
      items: [
        {
          name: 'Team Building',
          desc: 'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
        },
        {
          name: 'IT Recruitment',
          desc: 'Developers, DevOps, QA, Product Managers, designers — we know the IT market inside out and speak the same language as candidates.',
        },
        {
          name: 'Career Consulting',
          desc: 'Career opportunity assessment, interview preparation, development strategy and market positioning.',
        },
      ],
    },
    ctaBanner: {
      title: 'Leave a Request',
      subtitle:
        "Tell us about your challenge — we'll get back to you within 15 minutes and propose a solution",
      button: 'Send Request',
    },
    faqItems: [
      {
        q: 'Who is this course for?',
        a: 'For specialists and managers who want to reach the next level: change company, grow into a leadership role, or significantly increase income. It is not for people looking for their first job.',
      },
      {
        q: 'How long does it take to complete?',
        a: 'The course consists of video lessons you take at your own pace. Most people finish in 3–4 weeks spending 2–3 hours per week.',
      },
      {
        q: 'How is this different from free advice online?',
        a: 'The course is built from the perspective of someone who has been making hiring decisions for 20 years. You see the selection process through the eyes of the employer and recruiter: what actually gets people rejected, how CVs are read, and what decides salary negotiations.',
      },
      {
        q: 'Is there feedback from the author?',
        a: 'Yes, on the plans that include mentoring. You get homework reviews and feedback sessions directly with Sergey Denisyuk. The number of sessions depends on the plan.',
      },
      {
        q: 'Do you guarantee employment?',
        a: 'No, and no honest course does — the final decision always rests with the employer. We provide the tools that materially increase both the number of interview invitations and the quality of the offers.',
      },
      {
        q: 'Can I preview it for free?',
        a: 'Yes. Leave a request and we will send the introductory lesson free of charge, so you can judge the format before paying.',
      },
    ],
  },
}

;(EN as Translations).hero_form = {
  title: 'Find the right specialist',
  subtitle: "Leave a request — we'll get in touch within 15 minutes",
  name_label: 'How should we address you?',
  name_placeholder: 'Your name',
  phone_label: 'Phone',
  position_label: 'Who are you looking for?',
  position_placeholder: 'Select a position',
  positions: [
    'CEO / Chief Executive Officer',
    'COO / Chief Operating Officer',
    'CFO / Chief Financial Officer',
    'CTO / Chief Technology Officer',
    'CMO / Chief Marketing Officer',
    'CHRO / Head of HR',
    'Head of Sales',
    'Sales Manager',
    'Marketing Manager',
    'Developer (Frontend / Backend / Fullstack)',
    'DevOps / SRE Engineer',
    'QA Engineer',
    'Product Manager',
    'UX/UI Designer',
    'Finance / Accountant',
    'Lawyer',
    'HR / Recruiter',
    'Full team (turnkey)',
    'Other position',
  ],
  submit: 'Find',
  submitting: 'Sending...',
  success_title: 'Request sent!',
  success_subtitle: "We'll get in touch within 15 minutes",
  privacy: 'By clicking "Find", you agree to our privacy policy',
  errors: {
    name: 'Please enter your name',
    phone_required: 'Please enter your phone number',
    phone_invalid: 'Invalid phone number',
    position: 'Please select a position',
  },
}

export const translations: Record<Lang, Translations> = { UA, RU, EN }

export function useTranslations(lang: Lang): Translations {
  return translations[lang]
}
